import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";


import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  InfoOutlined,
  ExpandMore as ExpandMoreIcon,
  Label,
} from "@mui/icons-material";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Menu,
  IconButton,
  TextField,
  Box,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { IoMdTrash } from "react-icons/io";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import ViewStreamIcon from "@mui/icons-material/ViewStream";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { v4 as uuidv4 } from "uuid";
import {
  CardPreview,
  convertTypeMatches,
  defaultCss,
  typeMatches,
} from "./integrationUtils";
import SuperAppButton from "../SuperAppButton";
import CustomTextEditor from "./CustomInputTextEditor";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
// Utility to create a block from JSON data
const createBlockFromJson = (item = {}, blockType = "", defaultCss = {}) => {
  if (!item || !blockType || !defaultCss[blockType]) {
    console.warn("Invalid input for createBlockFromJson:", { item, blockType });
    return null;
  }

  const block = {
    id: item.id || `block-${uuidv4()}`,
    type: blockType,
    display_name: item.display_name || item.displayName || "",
    data: item.data || { elements: [] },
    css: item.css || {
      [blockType]: { ...defaultCss[blockType] },
      displayName: { ...defaultCss.displayName },
      displayNameContainer: { ...defaultCss.displayNameContainer },
    },
    outerCss: item.outerCss || { ...defaultCss.outer },
    default_fields: item.default_fields || {},
    is_default_value: !!item.is_default_value,
    add_display_name: item.add_display_name ?? true,
    // path: item.path || null,
    ...item,
  };

  if (Array.isArray(item.data?.elements) && item.data.elements.length) {
    return block;
  }

  const { value, display_name: name = "" } = item;

  const addElement = (field, val) =>
    block.data.elements.push({ field, value: val });
  const addDisplayName = () =>
    block.add_display_name && addElement("display_name", name);

  switch (blockType) {
    case "header":
    case "paragraph":
    case "quote":
      addElement("text", value || "");
      addDisplayName();
      break;
    case "button":
      addElement("text", name || "Click me");
      addElement("url", value || "");
      addDisplayName();
      break;
    case "list":
      addElement("items", Array.isArray(value) ? value : value ? [value] : []);
      addDisplayName();
      break;
    case "image":
      addElement(
        "files",
        (Array.isArray(value) ? value : value ? [value] : []).map((url) => ({
          url: url || "",
        }))
      );
      addElement("caption", name || "");
      addDisplayName();
      break;
    case "form":
      addElement("title", name || "Form");
      addElement(
        "fields",
        Array.isArray(value)
          ? value.map((field) => ({
              label: field.label || "",
              name: field.name || (field.label || "").toLowerCase(),
              field_type: field.field_type || "text",
              value: field.value || "",
              default_value: field.default_value || "",
              required: !!field.required,
              placeholder:
                field.placeholder ||
                `Enter ${(field.label || "").toLowerCase()}`,
            }))
          : []
      );
      addElement("submitText", "Submit");
      addDisplayName();
      break;
    case "card":
      addElement("title", name || "");
      addElement("description", value || "");
      addDisplayName();
      break;
    case "map":
      addElement("lat", item.lat ?? null);
      addElement("lng", item.lng ?? null);
      addDisplayName();
      break;
    case "table":
      addElement("headers", Array.isArray(item.headers) ? item.headers : []);
      addElement("rows", Array.isArray(item.rows) ? item.rows : []);
      addDisplayName();
      break;
    case "faq":
      addElement(
        "items",
        Array.isArray(value)
          ? value
          : value
          ? [{ question: name, answer: value }]
          : []
      );
      addDisplayName();
      break;
    case "section":
      addElement("children", Array.isArray(value) ? value : []);
      addDisplayName();
      break;
    default:
      console.warn(`Unsupported block type: ${blockType}`);
      return null;
  }

  return block.data.elements.length ? block : null;
};

// Transform initial JSONs into UI data
const editUiDataWithInitialJsons = (ui_data = {}, initialJsons = []) => {
  if (!Array.isArray(initialJsons) || !initialJsons.length)
    return { ...ui_data, blocks: [] };

  const updatedData = { ...ui_data, blocks: [] };
  let mapblock = { lat: null, lng: null };
  let latPath = "";
  let lngPath = "";

  initialJsons.forEach((item) => {
    if (!item) return;
    if (item.display_type === "latitude") {
      latPath = item.label || "";
      mapblock.lat = isFinite(parseFloat(item.value))
        ? parseFloat(item.value)
        : null;
    }
    if (item.display_type === "longitude") {
      lngPath = item.label || "";
      mapblock.lng = isFinite(parseFloat(item.value))
        ? parseFloat(item.value)
        : null;
    }
  });

  const blocks = initialJsons.reduce((acc, item) => {
    if (!item) return acc;
    const blockType = convertTypeMatches(item.display_type || item.type);
    if (!blockType) return acc;

    const block = createBlockFromJson(item, blockType, defaultCss);
    return block ? [...acc, block] : acc;
  }, []);

  if (
    mapblock.lat !== null &&
    mapblock.lng !== null &&
    !blocks.some((b) => b.type === "map")
  ) {
    blocks.push({
      id: `block-${uuidv4()}`,
      type: "map",
      display_name: "Venue Location",
      data: {
        elements: [
          { field: "lat", value: mapblock.lat },
          { field: "lng", value: mapblock.lng },
          { field: "display_name", value: "Venue Location" },
        ],
      },
      css: {
        map: { ...defaultCss.map },
        displayName: { ...defaultCss.displayName },
        displayNameContainer: { ...defaultCss.displayNameContainer },
      },
      outerCss: { ...defaultCss.outer },
      default_fields: {},
      is_default_value: false,
      add_display_name: true,
      path: { lat: latPath, lng: lngPath },
    });
  }

  updatedData.blocks = blocks;
  return updatedData;
};

export class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{ padding: "20px", color: "#fff", backgroundColor: "#d32f2f" }}
        >
          <h3>Something went wrong.</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
          {this.state.errorInfo && (
            <details style={{ whiteSpace: "pre-wrap" }}>
              <summary>Stack Trace</summary>
              <pre>{this.state.errorInfo.componentStack}</pre>
            </details>
          )}
          <Button
            variant="contained"
            onClick={() =>
              this.setState({ hasError: false, error: null, errorInfo: null })
            }
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </div>
      );
    }
    return this.props.children || null;
  }
}

const BlockEditorModal = React.memo(
  ({ show = false, onHide = () => {}, blocks = [], parentCss }) => {
    return (
      <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth>
        <DialogContent>
          <CardPreview
            blocks={Array.isArray(blocks) ? blocks : []}
            parentCss={parentCss}
          />
        </DialogContent>
        <DialogActions>
          <SuperAppButton onClick={onHide}>Close</SuperAppButton>
        </DialogActions>
      </Dialog>
    );
  }
);

const ExportModal = React.memo(
  ({ show = false, onHide = () => {}, jsonData = "" }) => (
    <Dialog open={show} onClose={onHide} maxWidth="lg" fullWidth>
      <DialogTitle>Exported Data</DialogTitle>
      <DialogContent>
        <pre
          style={{
            background: "#f8f9fa",
            padding: "16px",
            borderRadius: "8px",
            maxHeight: "500px",
            overflow: "auto",
          }}
        >
          {jsonData || "No data to display"}
        </pre>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onHide}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
);

const AddMoreModal = React.memo(
  ({
    show = false,
    onHide = () => {},
    initialJsons2 = [],
    onAdd = () => {},
    blocks = [],
  }) => {
    console.log("🚀 ~ initialJsons2:", initialJsons2)
    const [selectedItems, setSelectedItems] = useState([]);

    useEffect(() => {
      if (Array.isArray(initialJsons2) && initialJsons2.length) {
        setSelectedItems(
          initialJsons2.map(
            (item) =>
              !blocks.some(
                (block) => block.display_name === (item?.display_name || "")
              )
          )
        );
      }
    }, [initialJsons2, blocks]);

    const handleSelectionChange = (index, checked) => {
      setSelectedItems((prev) =>
        prev.map((item, i) => (i === index ? checked : item))
      );
    };

    const handleAddSelected = () => {
      if (!Array.isArray(initialJsons2)) return;
      onAdd(initialJsons2.filter((_, index) => selectedItems[index]));
      onHide();
    };

    return (
      <Dialog open={show} onClose={onHide} maxWidth="md" fullWidth>
        <DialogTitle>Select Items to Add</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              textTransform: "capitalize",
            }}
          >
            {Array.isArray(initialJsons2) &&
              initialJsons2.map((item, index) => {
                if (!item) return null;
                const blockType = convertTypeMatches(
                  item.display_type || item.type || ""
                );
                if (!blockType) return null;

                const block = createBlockFromJson(item, blockType, defaultCss);
                if (!block) return null;

                return (
                  <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedItems[index] || false}
                            onChange={(e) =>
                              handleSelectionChange(index, e.target.checked)
                            }
                          />
                        }
                        label={item.display_name || "Unnamed Item"}
                      />
                      <CardPreview blocks={[block]} />
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddSelected}
          >
            Add Selected
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const findMentionsLooping = (inputString, mainData) => {
  try {
    const parsedInput =
      typeof inputString === "string" ? JSON.parse(inputString) : inputString;

    function resolvePath(obj, path) {
      const parts = path
        .replace(/\[(\d+)\]/g, (match, p1) => `.${p1}`)
        .split(".");
      let resultData = parts.reduce((acc, part) => acc && acc[part], obj);
      return resultData !== undefined ? resultData : "";
    }

    let finalOutput = "";

    for (const item of parsedInput) {
      if (item.type === "text" && item.text) {
        finalOutput += item.text;
      } else if (item.type === "mention" && item.attrs && item.attrs.id) {
        const resolvedValue = resolvePath(mainData, item.attrs.id);
        finalOutput += resolvedValue !== undefined ? resolvedValue : "";
      }
    }
    return finalOutput || "No valid data processed";
  } catch (error) {
    console.error("Error in findMentionsLooping:", error);
    return inputString || ""; // Return empty string if input is invalid
  }
};


function getArrayPaths(obj, currentPath = '@', seenPaths = new Set()) {
  let paths = [];

  // Detect Arrays
  if (Array.isArray(obj)) {
    if (currentPath !== '@' && !seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push({ id: currentPath, label: currentPath });
    }

    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      paths = paths.concat(getArrayPaths(item, itemPath, seenPaths));
    });

  // Detect Objects
  } 
  
  else if (typeof obj === 'object' && obj !== null) {
    if (currentPath !== '@' && !seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push({ id: currentPath, label: currentPath });
    }

    Object.keys(obj).forEach(key => {
      const keyPath = currentPath === '@' ? key : `${currentPath}.${key}`;
      paths = paths.concat(getArrayPaths(obj[key], keyPath, seenPaths));
    });
  }

  return paths;
}
function getValueFromPath(path, obj) {
      const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.');
      return parts.reduce((acc, part) => acc && acc[part], obj);
    }

const BlockEditModal = React.memo(
  ({
    show = false,
    onHide = () => {},
    block = null,
    blockIndex = null,
    updateBlock = () => {},
    apiData = {},
    primaryPath = "",
  }) => {
    const [defaultValue, setDefaultValue] = React.useState("");
    const [makeDefault, setMakeDefault] = React.useState(false);
    const [currentValue, setCurrentValue] = React.useState("");
    const [mapLat, setMapLat] = React.useState("");
    const [mapLng, setMapLng] = React.useState("");
    const [formFields, setFormFields] = React.useState([]);
    const [imageUrls, setImageUrls] = React.useState("");
    const [listItems, setListItems] = React.useState("");
    const [faqItems, setFaqItems] = React.useState([]);
    const [tableHeaders, setTableHeaders] = React.useState("");
    const [tableRows, setTableRows] = React.useState("");
    const [editDisplayName, setEditDisplayName] = React.useState(true);
    const [display_name, setDisplayName] = React.useState("");
    const [childrenBlocks, setChildrenBlocks] = React.useState([]);
    const [path, setPath] = React.useState("");
    const [pathValue, setPathValue] = React.useState("");
    const [loopingPath, setLoopingPath] = React.useState("");
function getArrayPaths(obj, currentPath = "@", seenPaths = new Set()) {
  let paths = [];
  if (Array.isArray(obj)) {
    const pathEntry = { id: currentPath, label: currentPath };
    if (!seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push(pathEntry);
    }
    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      paths = paths.concat(getArrayPaths(item, itemPath, seenPaths));
    });
  } else if (typeof obj === "object" && obj !== null) {
    Object.keys(obj).forEach((key) => {
      const keyPath = currentPath === "@" ? key : `${currentPath}.${key}`;
      paths = paths.concat(getArrayPaths(obj[key], keyPath, seenPaths));
    });
  }
  return paths;
}
    React.useEffect(() => {
      if (!block || blockIndex === null) return;

      const elements = Array.isArray(block.data?.elements)
        ? block.data.elements
        : [];
      setMakeDefault(!!block.is_default_value);
      setEditDisplayName(block.add_display_name ?? true);
      setDisplayName(block.display_name || "");
      setPath(block.path || "");
      setLoopingPath(block?.loopingPath || "");

      const getElementValue = (field) =>
        elements.find((el) => el?.field === field)?.value || "";

      switch (block.type) {
        case "header":
        case "paragraph":
        case "quote":
        case "card":
          setDefaultValue(block.default_fields?.text || "");
          setCurrentValue(
            getElementValue(block.type === "card" ? "description" : "text")
          );
          break;
        case "button":
          setDefaultValue(block.default_fields?.url || "");
          setCurrentValue(getElementValue("url"));
          break;
        case "list":
          setDefaultValue(
            Array.isArray(block.default_fields?.items)
              ? block.default_fields.items.join("\n")
              : ""
          );
          setListItems(
            Array.isArray(getElementValue("items"))
              ? getElementValue("items").join("\n")
              : ""
          );
          break;
        case "image":
          setDefaultValue(
            Array.isArray(block.default_fields?.files)
              ? block.default_fields.files.join("\n")
              : ""
          );
          setImageUrls(
            Array.isArray(getElementValue("files"))
              ? getElementValue("files")
                  .map((file) => file?.url || "")
                  .join("\n")
              : ""
          );
          break;
        case "form":
          setFormFields(
            Array.isArray(getElementValue("fields"))
              ? getElementValue("fields").map((field) => ({
                  ...field,
                  default_value: field.default_value || "",
                  value: field.value || "",
                }))
              : []
          );
          setDefaultValue(
            Array.isArray(getElementValue("fields"))
              ? getElementValue("fields")
                  .map((f) => f?.default_value || "")
                  .join("\n")
              : ""
          );
          break;
        case "map":
          setDefaultValue(
            block.default_fields?.lat && block.default_fields?.lng
              ? `${block.default_fields.lat},${block.default_fields.lng}`
              : ""
          );
          setMapLat(getElementValue("lat") || "0");
          setMapLng(getElementValue("lng") || "0");
          break;
        case "table":
          setDefaultValue(
            Array.isArray(block.default_fields?.headers)
              ? block.default_fields.headers.join(",")
              : ""
          );
          setTableHeaders(
            Array.isArray(getElementValue("headers"))
              ? getElementValue("headers").join(",")
              : ""
          );
          setTableRows(
            Array.isArray(getElementValue("rows"))
              ? getElementValue("rows")
                  .map((row) => (Array.isArray(row) ? row.join(",") : ""))
                  .join("\n")
              : ""
          );
          break;
        case "faq":
          setDefaultValue(
            Array.isArray(block.default_fields?.items)
              ? block.default_fields.items
                  .map(
                    (item) => `${item?.question || ""}: ${item?.answer || ""}`
                  )
                  .join("\n")
              : ""
          );
          setFaqItems(
            Array.isArray(getElementValue("items"))
              ? getElementValue("items")
              : []
          );
          break;
        case "section":
          setChildrenBlocks(
            Array.isArray(getElementValue("children"))
              ? getElementValue("children")
              : []
          );
          setDefaultValue("");
          break;
        default:
          setDefaultValue("");
          setCurrentValue("");
      }
    }, [block, blockIndex, apiData, primaryPath]);

    useEffect(() => {
      let resolvedPath = path || "";
      try {
        const parsedPath = JSON.parse(path);
        resolvedPath = findMentionsLooping(parsedPath, apiData);
      } catch (error) {
        console.error("Error resolving path:", error);
        // Use original path if parsing fails
      }
      setPathValue(resolvedPath || "");
    }, [path, apiData]);

    const handleSave = () => {
      if (!block || blockIndex === null) {
        console.warn("Block or blockIndex is missing");
        return;
      }

      let updatedBlock = { ...block, data: { ...block.data } };
      let updatedElements = Array.isArray(block.data?.elements)
        ? block.data.elements.filter((el) => el?.field !== "display_name")
        : [];

      const updateElement = (field, value) => {
        const index = updatedElements.findIndex((el) => el?.field === field);
        if (index !== -1) {
          updatedElements[index] = { ...updatedElements[index], value };
        } else {
          updatedElements.push({ field, value });
        }
      };

      // Update path
      updatedBlock.path = path || "";

      switch (block.type) {
        case "header":
        case "paragraph":
        case "quote":
        case "card": {
          const field = block.type === "card" ? "description" : "text";
          updateElement(field, pathValue || currentValue || "");
          updatedBlock.default_fields = { text: defaultValue || "" };
          break;
        }
        case "button": {
          updateElement("url", pathValue || currentValue || "");
          updatedBlock.default_fields = { url: defaultValue || "" };
          break;
        }
        case "list": {
          const listArray = (pathValue || listItems || "")
            .split("\n")
            .filter((item) => item.trim());
          updateElement("items", listArray);
          updatedBlock.default_fields = {
            items: (defaultValue || "")
              .split("\n")
              .filter((item) => item.trim()),
          };
          break;
        }
        case "image": {
          const urlArray = (pathValue || imageUrls || "")
            .split("\n")
            .filter((url) => url.trim());
          updateElement(
            "files",
            urlArray.map((url) => ({ url }))
          );
          updatedBlock.default_fields = {
            files: (defaultValue || "").split("\n").filter((url) => url.trim()),
          };
          break;
        }
        case "form": {
          const updatedFields = Array.isArray(formFields)
            ? formFields.map((field) => ({
                ...field,
                default_value: field.default_value || "",
                value: pathValue || field.value || "",
              }))
            : [];
          updateElement("fields", updatedFields);
          updatedBlock.default_fields = {
            fields: updatedFields,
          };
          break;
        }
        case "map": {
          const [lat = "0", lng = "0"] = (
            pathValue || `${mapLat || "0"},${mapLng || "0"}`
          ).split(",");
          const parsedLat = isFinite(parseFloat(lat)) ? parseFloat(lat) : 0;
          const parsedLng = isFinite(parseFloat(lng)) ? parseFloat(lng) : 0;
          updateElement("lat", parsedLat);
          updateElement("lng", parsedLng);
          updatedBlock.default_fields = {
            lat: isFinite(
              parseFloat((defaultValue || "").split(",")[0]?.trim())
            )
              ? parseFloat((defaultValue || "").split(",")[0]?.trim())
              : 0,
            lng: isFinite(
              parseFloat((defaultValue || "").split(",")[1]?.trim())
            )
              ? parseFloat((defaultValue || "").split(",")[1]?.trim())
              : 0,
          };
          break;
        }
        case "table": {
          const headerArray = (pathValue || tableHeaders || "")
            .split(",")
            .filter((h) => h.trim());
          const rowArray = (tableRows || "")
            .split("\n")
            .map((row) => row.split(",").filter((cell) => cell.trim()))
            .filter((row) => row.length);
          updateElement("headers", headerArray);
          updateElement("rows", rowArray);
          updatedBlock.default_fields = {
            headers: (defaultValue || "").split(",").filter((h) => h.trim()),
          };
          break;
        }
        case "faq": {
          const faqArray = (pathValue || "")
            .split("\n")
            .map((item) => {
              const [question = "", answer = ""] = item
                .split(":")
                .map((s) => s.trim());
              return { question, answer };
            })
            .filter((item) => item.question && item.answer);
          updateElement("items", faqArray.length ? faqArray : faqItems);
          updatedBlock.default_fields = {
            items: (defaultValue || "")
              .split("\n")
              .map((item) => {
                const [question = "", answer = ""] = item
                  .split(":")
                  .map((s) => s.trim());
                return { question, answer };
              })
              .filter((item) => item.question && item.answer),
          };
          break;
        }
        case "section": {
          updateElement(
            "children",
            Array.isArray(childrenBlocks) ? childrenBlocks : []
          );
          updatedBlock.default_fields = { children: [] };
          break;
        }
        default:
          console.warn(`Unhandled block type in handleSave: ${block.type}`);
      }

      if (editDisplayName) {
        updateElement("display_name", display_name || "");
      }

      updatedBlock.is_default_value = !!makeDefault;
      updatedBlock.add_display_name = !!editDisplayName;
      updatedBlock.display_name = display_name || "";
      updatedBlock.data.elements = updatedElements;

      if (makeDefault) {
        switch (block.type) {
          case "header":
          case "paragraph":
          case "quote":
          case "card":
            updatedBlock.default_fields.text = pathValue || currentValue || "";
            break;
          case "button":
            updatedBlock.default_fields.url = pathValue || currentValue || "";
            break;
          case "list":
            updatedBlock.default_fields.items = (pathValue || listItems || "")
              .split("\n")
              .filter((item) => item.trim());
            break;
          case "image":
            updatedBlock.default_fields.files = (pathValue || imageUrls || "")
              .split("\n")
              .filter((url) => url.trim());
            break;
          case "form":
            updatedBlock.default_fields.fields = Array.isArray(formFields)
              ? formFields.map((field) => ({
                  ...field,
                  default_value: pathValue || field.value || "",
                }))
              : [];
            break;
          case "map":
            const [defaultLat = "0", defaultLng = "0"] = (
              pathValue || `${mapLat || "0"},${mapLng || "0"}`
            ).split(",");
            updatedBlock.default_fields.lat = isFinite(parseFloat(defaultLat))
              ? parseFloat(defaultLat)
              : 0;
            updatedBlock.default_fields.lng = isFinite(parseFloat(defaultLng))
              ? parseFloat(defaultLng)
              : 0;
            break;
          case "table":
            updatedBlock.default_fields.headers = (
              pathValue ||
              tableHeaders ||
              ""
            )
              .split(",")
              .filter((h) => h.trim());
            break;
          case "faq":
            const defaultFaqArray = (pathValue || "")
              .split("\n")
              .map((item) => {
                const [question = "", answer = ""] = item
                  .split(":")
                  .map((s) => s.trim());
                return { question, answer };
              })
              .filter((item) => item.question && item.answer);
            updatedBlock.default_fields.items = defaultFaqArray.length
              ? defaultFaqArray
              : faqItems;
            break;
          case "section":
            updatedBlock.default_fields.children = Array.isArray(childrenBlocks)
              ? childrenBlocks
              : [];
            break;
        }
      }
      
      try {
        updatedBlock["loopingPath"] = loopingPath
        updateBlock(blockIndex, updatedBlock);
        onHide();
      } catch (error) {
        console.error("Error updating block:", error);
      }
    };

    const handleFormFieldChange = (index, field, value) => {
      setFormFields((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      );
    };

    const handleFaqItemChange = (index, field, value) => {
      setFaqItems((prev) =>
        prev.map((item, i) =>
          i === index ? { ...item, [field]: value } : item
        )
      );
    };

    const handlePathChange = (e) => {
      const newPath = e.target.value || "";
      setPath(newPath);
      let resolvedPath = newPath;
      try {
        const parsedPath = JSON.parse(newPath);
        resolvedPath = findMentionsLooping(parsedPath, apiData);
      } catch (error) {
        console.error("Error parsing path in handlePathChange:", error);
      }
      setPathValue(resolvedPath || "");
    };

    return !block || blockIndex === null ? null : (
      <Dialog
        open={show}
        onClose={onHide}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            background: "rgb(32, 35, 69)",
            color: "#fff",
            zIndex: 1300000000,
          },
        }}
      >
        <DialogTitle sx={{ color: "#fff" }}>
          Edit Block: {block.display_name || "Unnamed"}
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormControl
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <FormLabel sx={{ color: "#fff", m: 0 }}>
                Make Existing Value Default
              </FormLabel>
              <Checkbox
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                sx={{ color: "#fff", "&.Mui-checked": { color: "#90caf9" } }}
              />
            </FormControl>
            <FormControl
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <FormLabel sx={{ color: "#fff", m: 0 }}>
                Include Display Name in Editor
              </FormLabel>
              <Checkbox
                checked={editDisplayName}
                onChange={(e) => setEditDisplayName(e.target.checked)}
                sx={{ color: "#fff", "&.Mui-checked": { color: "#90caf9" } }}
              />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel sx={{ color: "#fff" }}>Name</FormLabel>
              <TextField
                value={display_name}
                onChange={(e) => setDisplayName(e.target.value || "")}
                placeholder="Enter display name"
                sx={{
                  input: { color: "#fff" },
                  bgcolor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "4px",
                }}
              />
            </FormControl>
            {/* <FormControl fullWidth>
              <FormLabel sx={{ color: '#fff' }}>Path</FormLabel>
              <TextField
                value={path}
                onChange={handlePathChange}
                placeholder="Enter path"
                sx={{
                  input: { color: '#fff' },
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                }}
              />
            </FormControl> */}
            {/* <FormControl fullWidth>
              <FormLabel sx={{ color: '#fff' }}>Path Value</FormLabel>
              <TextField
                value={pathValue}
                InputProps={{ readOnly: true }}
                multiline
                rows={3}
                sx={{
                  textarea: { color: '#fff' },
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                }}
              />
            </FormControl> */}

            {/* Looping path starts */}
            <h6>Mention you looping path for your image</h6>
            <Autocomplete
              options={getArrayPaths(apiData)}
              getOptionLabel={(option) => option.label}
              value={getArrayPaths(apiData).find(p => p.id === loopingPath) || null}
              onChange={(e, newValue) => {
                setLoopingPath(newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="Select Path" />
              )}
              renderOption={(props, option) => (
                <Tooltip
                  title={`Path Value: ${JSON.stringify(getValueFromPath(option.label, apiData), null, 2)}...`}
                  placement="right"
                  sx={{ background: "white",overflow:"auto" }}
                  arrow
                  key={JSON.stringify(option)}
                >
                  <li {...props}>{option.label}</li>
                </Tooltip>
              )}
              style={{ minWidth: "200px", overflow: "auto" }}
            />
            {/* Looping path ends */}
            <Typography sx={{ color: "#fff", marginBlock: 0 }}>Path</Typography>
            <CustomTextEditor
              handleTitleChange={(e) => setPath(e.target.value || "")}
              prompt={path}
              sampleData={apiData}
              primaryPath={primaryPath}
            />
            {block.type === "map" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default Latitude,Longitude
                  </FormLabel>
                  <TextField
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="e.g., 40.7128,-74.0060"
                    sx={{
                      input: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                    <FormLabel sx={{ color: "#fff" }}>Latitude</FormLabel>
                    <TextField
                      type="number"
                      value={mapLat}
                      onChange={(e) => setMapLat(e.target.value || "")}
                      placeholder="Enter latitude"
                      sx={{
                        input: { color: "#fff" },
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                      }}
                    />
                  </FormControl>
                  <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                    <FormLabel sx={{ color: "#fff" }}>Longitude</FormLabel>
                    <TextField
                      type="number"
                      value={mapLng}
                      onChange={(e) => setMapLng(e.target.value || "")}
                      placeholder="Enter longitude"
                      sx={{
                        input: { color: "#fff" },
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "4px",
                      }}
                    />
                  </FormControl>
                </Box>
              </>
            ) : block.type === "image" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default Image URLs (one url per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="Enter default image URLs"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Image URLs (one per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    value={imageUrls}
                    onChange={(e) => setImageUrls(e.target.value || "")}
                    placeholder="Enter image URLs"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
              </>
            ) : block.type === "form" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default Field Values (one per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="Enter default field values"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormLabel sx={{ color: "#fff" }}>Form Fields</FormLabel>
                {Array.isArray(formFields) &&
                  formFields.map((field, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        p: "16px",
                        borderRadius: "4px",
                        mb: "8px",
                      }}
                    >
                      <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                        <FormLabel sx={{ color: "#fff" }}>Label</FormLabel>
                        <TextField
                          value={field.label || ""}
                          onChange={(e) =>
                            handleFormFieldChange(
                              index,
                              "label",
                              e.target.value || ""
                            )
                          }
                          sx={{
                            input: { color: "#fff" },
                            bgcolor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                        <FormLabel sx={{ color: "#fff" }}>
                          Default Value
                        </FormLabel>
                        <TextField
                          value={field.default_value || ""}
                          onChange={(e) =>
                            handleFormFieldChange(
                              index,
                              "default_value",
                              e.target.value || ""
                            )
                          }
                          sx={{
                            input: { color: "#fff" },
                            bgcolor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                        <FormLabel sx={{ color: "#fff" }}>Value</FormLabel>
                        <TextField
                          value={field.value || ""}
                          onChange={(e) =>
                            handleFormFieldChange(
                              index,
                              "value",
                              e.target.value || ""
                            )
                          }
                          sx={{
                            input: { color: "#fff" },
                            bgcolor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel sx={{ color: "#fff" }}>Required</FormLabel>
                        <Checkbox
                          checked={!!field.required}
                          onChange={(e) =>
                            handleFormFieldChange(
                              index,
                              "required",
                              e.target.checked
                            )
                          }
                          sx={{
                            color: "#fff",
                            "&.Mui-checked": { color: "#90caf9" },
                          }}
                        />
                      </FormControl>
                    </Box>
                  ))}
              </>
            ) : block.type === "list" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default List Items (one per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={3}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="Enter default list items"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    List Items (one per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={3}
                    value={listItems}
                    onChange={(e) => setListItems(e.target.value || "")}
                    placeholder="Enter list items"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
              </>
            ) : block.type === "faq" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default FAQ Items (question: answer per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="e.g., Question: Answer"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormLabel sx={{ color: "#fff" }}>FAQ Items</FormLabel>
                {Array.isArray(faqItems) &&
                  faqItems.map((item, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        gap: "16px",
                        flexWrap: "wrap",
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                        p: "16px",
                        borderRadius: "4px",
                        mb: "8px",
                      }}
                    >
                      <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                        <FormLabel sx={{ color: "#fff" }}>Question</FormLabel>
                        <TextField
                          value={item?.question || ""}
                          onChange={(e) =>
                            handleFaqItemChange(
                              index,
                              "question",
                              e.target.value || ""
                            )
                          }
                          sx={{
                            input: { color: "#fff" },
                            bgcolor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                        <FormLabel sx={{ color: "#fff" }}>Answer</FormLabel>
                        <TextField
                          value={item?.answer || ""}
                          onChange={(e) =>
                            handleFaqItemChange(
                              index,
                              "answer",
                              e.target.value || ""
                            )
                          }
                          sx={{
                            input: { color: "#fff" },
                            bgcolor: "rgba(255, 255, 255, 0.1)",
                            borderRadius: "4px",
                          }}
                        />
                      </FormControl>
                    </Box>
                  ))}
              </>
            ) : block.type === "table" ? (
              <>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Default Headers (comma-separated)
                  </FormLabel>
                  <TextField
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="e.g., Header1,Header2"
                    sx={{
                      input: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Headers (comma-separated)
                  </FormLabel>
                  <TextField
                    value={tableHeaders}
                    onChange={(e) => setTableHeaders(e.target.value || "")}
                    placeholder="e.g., Header1,Header2"
                    sx={{
                      input: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormControl fullWidth>
                  <FormLabel sx={{ color: "#fff" }}>
                    Rows (comma-separated, one row per line)
                  </FormLabel>
                  <TextField
                    multiline
                    rows={4}
                    value={tableRows}
                    onChange={(e) => setTableRows(e.target.value || "")}
                    placeholder="e.g., Cell1,Cell2"
                    sx={{
                      textarea: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
              </>
            ) : block.type === "section" ? (
              <>
                <FormLabel sx={{ color: "#fff" }}>Child Blocks</FormLabel>
                {Array.isArray(childrenBlocks) && childrenBlocks.length ? (
                  childrenBlocks.map((child, index) => (
                    <Box
                      key={child.id || index}
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography sx={{ color: "#fff" }}>
                        {child.display_name || child.type || "Unnamed"}
                      </Typography>
                      <CardPreview blocks={[child]} />
                    </Box>
                  ))
                ) : (
                  <Typography sx={{ color: "#fff" }}>
                    No child blocks
                  </Typography>
                )}
              </>
            ) : (
              <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                  <FormLabel sx={{ color: "#fff" }}>Default Value</FormLabel>
                  <TextField
                    value={defaultValue}
                    onChange={(e) => setDefaultValue(e.target.value || "")}
                    placeholder="Enter default value"
                    sx={{
                      input: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
                <FormControl sx={{ flex: 1, minWidth: "200px" }}>
                  <FormLabel sx={{ color: "#fff" }}>Current Value</FormLabel>
                  <TextField
                    value={currentValue}
                    onChange={(e) => setCurrentValue(e.target.value || "")}
                    placeholder="Enter current value"
                    sx={{
                      input: { color: "#fff" },
                      bgcolor: "rgba(255, 255, 255, 0.1)",
                      borderRadius: "4px",
                    }}
                  />
                </FormControl>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "rgb(32, 35, 69)", padding: "16px" }}>
          <Button
            variant="outlined"
            onClick={onHide}
            sx={{
              color: "#fff",
              borderColor: "#fff",
              "&:hover": {
                borderColor: "#90caf9",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

const CssEditor = React.memo(
  ({
    selectedBlock = null,
    updateBlock = () => {},
    parentCss = {},
    updateParentCss = () => {},
    setcardWidthType= () => {},
              cardWidthType=null
  }) => {
    const formatPropertyName = (name) =>
      typeof name === "string" ? name.replace(/([A-Z])/g, " $1").trim() : "";

    // State to track added properties for each section
    const [addedProperties, setAddedProperties] = useState({
      block: [],
      outer: [],
      parent: [],
      merged: [],
      displayName: [],
      displayNameContainer: [],
      children: {},
    });

    // State to track deleted default properties for each section
    const [deletedProperties, setDeletedProperties] = useState({
      block: [],
      outer: [],
      parent: [],
      merged: [],
      displayName: [],
      displayNameContainer: [],
      children: {},
    });

    // State to control Menu anchor element
    const [anchorEl, setAnchorEl] = useState({
      block: null,
      outer: null,
      parent: null,
      merged: null,
      displayName: null,
      displayNameContainer: null,
      children: {},
    });

    // Alignment options
    const alignmentOptions = useMemo(
      () => [
        {
          label: "Unset",
          value: "unset",
          css: {
            display: undefined,
            justifyContent: undefined,
            alignItems: undefined,
            flexWrap: undefined,
            flexDirection: undefined,
            gap: undefined,
            gridTemplateColumns: undefined,
          },
          tooltip: "Clears all alignment settings.",
        },
        {
          label: "Default",
          value: "default",
          css: {},
          tooltip: "Uses default browser styles.",
        },
        {
          label: "Center Vertically & Horizontally",
          value: "center",
          css: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
          },
          tooltip: "Centers content both vertically and horizontally.",
        },
        {
          label: "Top Left",
          value: "top-left",
          css: {
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
          },
          tooltip: "Aligns content to the top-left.",
        },
        {
          label: "Top Center",
          value: "top-center",
          css: {
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          },
          tooltip: "Aligns content to the top, centered horizontally.",
        },
        {
          label: "Top Right",
          value: "top-right",
          css: {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-start",
          },
          tooltip: "Aligns content to the top-right.",
        },
        {
          label: "Bottom Left",
          value: "bottom-left",
          css: {
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-end",
          },
          tooltip: "Aligns content to the bottom-left.",
        },
        {
          label: "Bottom Center",
          value: "bottom-center",
          css: {
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-end",
          },
          tooltip: "Aligns content to the bottom, centered horizontally.",
        },
        {
          label: "Bottom Right",
          value: "bottom-right",
          css: {
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "flex-end",
          },
          tooltip: "Aligns content to the bottom-right.",
        },
        {
          label: "Spread Evenly Horizontally",
          value: "space-evenly-h",
          css: {
            display: "flex",
            justifyContent: "space-evenly",
            alignItems: "center",
            gap: "16px",
          },
          tooltip: "Spreads content evenly horizontally.",
        },
        {
          label: "Space Between Horizontally",
          value: "space-between-h",
          css: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
          },
          tooltip: "Places content at edges with equal space between.",
        },
        {
          label: "Space Around Horizontally",
          value: "space-around-h",
          css: {
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            gap: "16px",
          },
          tooltip: "Distributes content with equal space around.",
        },
        {
          label: "Stack Vertically",
          value: "vertical-stack",
          css: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            gap: "16px",
          },
          tooltip: "Stacks content vertically.",
        },
        {
          label: "Vertical Center",
          value: "vertical-center",
          css: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
          },
          tooltip: "Centers content vertically in a column.",
        },
        {
          label: "Grid with Equal Columns",
          value: "grid-equal",
          css: {
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px",
          },
          tooltip: "Arranges content in a grid with equal columns.",
        },
      ],
      []
    );

    // Card width type options
    const cardWidthTypeOptions = [
      { value: "full-width-card", label: "Full Width Card", css: { width: "100%" } },
      { value: "grid", label: "Grid", css: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "16px" } },
    ];

    // Common spacing options (numeric values only)
    const spacingOptions = [0, 4, 8, 12, 16, 24, 32, 40, 48];

    // Default visible properties for each section
    const defaultVisibleProperties = {
      block: [
        "fontSize",
        "color",
        "paddingTop",
        "paddingBottom",
        "marginTop",
        "marginBottom",
      ],
      outer: [
        "backgroundColor",
        "paddingTop",
        "paddingBottom",
        "marginTop",
        "marginBottom",
      ],
      parent: [
        "backgroundColor",
        "paddingTop",
        "paddingBottom",
        "marginTop",
        "marginBottom",
      ],
      merged: [
        "backgroundColor",
        "paddingTop",
        "paddingBottom",
        "marginTop",
        "marginBottom",
      ],
      displayName: ["fontSize", "color", "paddingTop", "paddingBottom"],
      displayNameContainer: ["backgroundColor", "paddingTop", "paddingBottom"],
    };

    // Default values for properties when added
    const defaultPropertyValues = {
      fontSize: "16px",
      fontFamily: "Arial, sans-serif",
      letterSpacing: "normal",
      textTransform: "none",
      textAlign: "left",
      lineHeight: "normal",
      textDecoration: "none",
      marginTop: "0px",
      marginRight: "0px",
      marginBottom: "0px",
      marginLeft: "0px",
      paddingTop: "0px",
      paddingRight: "0px",
      paddingBottom: "0px",
      paddingLeft: "0px",
      borderWidth: "0px",
      borderStyle: "none",
      borderColor: "#000000",
      borderRadius: "0px",
      backgroundColor: "#ffffff",
      backgroundClip: "border-box",
      opacity: "1",
      boxShadow: "none",
      filter: "none",
      position: "static",
      zIndex: "0",
      top: "0px",
      left: "0px",
      width: "auto",
      height: "auto",
      maxWidth: "none",
      minHeight: "auto",
      transition: "none",
      transform: "none",
      cursor: "auto",
      overflow: "visible",
      objectFit: "cover",
      objectPosition: "center",
      borderCollapse: "separate",
      tableLayout: "auto",
      flexWrap: "nowrap",
      alignContent: "flex-start",
      gap: "0px",
      display: "block",
      justifyContent: "flex-start",
      alignItems: "flex-start",
    };

    // CSS properties (dynamic based on block type)
    const getCssProperties = (blockType = "") => [
      // Typography
      {
        name: "fontSize",
        options: [
          "12px",
          "14px",
          "16px",
          "18px",
          "20px",
          "24px",
          "28px",
          "32px",
          "36px",
          "40px",
        ],
        tooltip: "Sets text size.",
      },
      {
        name: "fontFamily",
        options: [
          "Arial, sans-serif",
          "Helvetica, sans-serif",
          "Georgia, serif",
          "Times New Roman, serif",
          "Verdana, sans-serif",
          "Roboto, sans-serif",
          "Lato, sans-serif",
          "Open Sans, sans-serif",
        ],
        tooltip: "Chooses text font.",
      },
      {
        name: "fontWeight",
        options: [
          "normal",
          "bold",
          "bolder",
          "lighter",
          "100",
          "200",
          "300",
          "400",
          "500",
          "600",
          "700",
          "800",
          "900",
        ],
        tooltip: "Sets text thickness.",
      },
      { name: "color", type: "color", tooltip: "Sets text color." },
      {
        name: "letterSpacing",
        options: ["normal", "0px", "1px", "2px", "3px", "4px", "5px"],
        tooltip: "Adjusts space between letters.",
      },
      {
        name: "textTransform",
        options: ["none", "uppercase", "lowercase", "capitalize"],
        tooltip: "Changes text case.",
      },
      {
        name: "textAlign",
        options: ["left", "center", "right", "justify"],
        tooltip: "Aligns text horizontally.",
      },
      {
        name: "lineHeight",
        options: ["normal", "1", "1.2", "1.5", "1.8", "2", "2.5"],
        tooltip: "Sets line spacing.",
      },
      {
        name: "textDecoration",
        options: ["none", "underline", "overline", "line-through"],
        tooltip: "Adds text decoration.",
      },
      // Spacing
      {
        name: "marginTop",
        options: spacingOptions,
        tooltip: "Sets top outer space.",
      },
      {
        name: "marginRight",
        options: spacingOptions,
        tooltip: "Sets right outer space.",
      },
      {
        name: "marginBottom",
        options: spacingOptions,
        tooltip: "Sets bottom outer space.",
      },
      {
        name: "marginLeft",
        options: spacingOptions,
        tooltip: "Sets left outer space.",
      },
      {
        name: "paddingTop",
        options: spacingOptions,
        tooltip: "Sets top inner space.",
      },
      {
        name: "paddingRight",
        options: spacingOptions,
        tooltip: "Sets right inner space.",
      },
      {
        name: "paddingBottom",
        options: spacingOptions,
        tooltip: "Sets bottom inner space.",
      },
      {
        name: "paddingLeft",
        options: spacingOptions,
        tooltip: "Sets left inner space.",
      },
      // Borders
      {
        name: "borderWidth",
        options: ["0px", "1px", "2px", "3px", "4px", "5px"],
        tooltip: "Sets border thickness.",
      },
      {
        name: "borderStyle",
        options: ["none", "solid", "dashed", "dotted", "double"],
        tooltip: "Chooses border style.",
      },
      { name: "borderColor", type: "color", tooltip: "Sets border color." },
      {
        name: "borderRadius",
        options: ["0px", "4px", "8px", "12px", "16px", "24px", "32px"],
        tooltip: "Rounds corners.",
      },
      // Background
      {
        name: "backgroundColor",
        type: "color",
        tooltip: "Sets background color.",
      },
      {
        name: "backgroundClip",
        options: ["border-box", "padding-box", "content-box"],
        tooltip: "Defines background extent.",
      },
      // Effects
      {
        name: "opacity",
        options: [
          "0",
          "0.1",
          "0.2",
          "0.3",
          "0.4",
          "0.5",
          "0.6",
          "0.7",
          "0.8",
          "0.9",
          "1",
        ],
        tooltip: "Sets transparency.",
      },
      {
        name: "boxShadow",
        options: [
          "none",
          "0 2px 4px rgba(0,0,0,0.1)",
          "0 4px 8px rgba(0,0,0,0.2)",
          "0 8px 16px rgba(0,0,0,0.3)",
          "0 12px 24px rgba(0,0,0,0.4)",
        ],
        tooltip: "Adds shadow.",
      },
      {
        name: "filter",
        options: [
          "none",
          "blur(2px)",
          "brightness(120%)",
          "contrast(150%)",
          "grayscale(100%)",
          "sepia(80%)",
        ],
        tooltip: "Applies visual effects.",
      },
      // Positioning
      {
        name: "position",
        options: ["static", "relative", "absolute", "fixed", "sticky"],
        tooltip: "Sets positioning.",
      },
      {
        name: "zIndex",
        options: ["0", "1", "10", "100", "1000", "10000"],
        tooltip: "Sets stacking order.",
      },
      {
        name: "top",
        options: ["0px", "10px", "20px", "50px", "100px"],
        tooltip: "Sets top distance.",
      },
      {
        name: "left",
        options: ["0px", "10px", "20px", "50px", "100px"],
        tooltip: "Sets left distance.",
      },
      // Sizing
      {
        name: "width",
        options: [
          "auto",
          "100%",
          "75%",
          "50%",
          "25%",
          "200px",
          "400px",
          "600px",
          "800px",
        ],
        tooltip: "Sets width.",
      },
      {
        name: "height",
        options: ["auto", "100px", "200px", "300px", "400px", "600px", "800px"],
        tooltip: "Sets height.",
      },
      {
        name: "maxWidth",
        options: ["none", "100%", "500px", "800px", "1200px"],
        tooltip: "Sets maximum width.",
      },
      {
        name: "minHeight",
        options: ["auto", "100px", "200px", "400px", "600px"],
        tooltip: "Sets minimum height.",
      },
      // Transitions
      {
        name: "transition",
        options: [
          "none",
          "all 0.2s ease",
          "all 0.3s ease",
          "all 0.5s ease",
          "all 0.2s linear",
          "all 0.3s linear",
        ],
        tooltip: "Adds transitions.",
      },
      {
        name: "transform",
        options: [
          "none",
          "rotate(5deg)",
          "rotate(10deg)",
          "scale(1.1)",
          "scale(0.9)",
          "skew(10deg)",
        ],
        tooltip: "Applies transformations.",
      },
      // Interactivity
      {
        name: "cursor",
        options: ["auto", "pointer", "default", "not-allowed", "move", "text"],
        tooltip: "Changes cursor.",
      },
      {
        name: "overflow",
        options: ["visible", "hidden", "scroll", "auto"],
        tooltip: "Controls overflow.",
      },
      // Type-specific
      ...(blockType === "image"
        ? [
            {
              name: "objectFit",
              options: ["cover", "contain", "fill", "none", "scale-down"],
              tooltip: "Controls image fit.",
            },
            {
              name: "objectPosition",
              options: ["center", "top", "bottom", "left", "right"],
              tooltip: "Sets image position.",
            },
          ]
        : []),
      ...(blockType === "table"
        ? [
            {
              name: "borderCollapse",
              options: ["collapse", "separate"],
              tooltip: "Controls table border merging.",
            },
            {
              name: "tableLayout",
              options: ["auto", "fixed"],
              tooltip: "Sets table layout.",
            },
          ]
        : []),
      ...(blockType === "section"
        ? [
            {
              name: "flexWrap",
              options: ["nowrap", "wrap", "wrap-reverse"],
              tooltip: "Controls flex wrap.",
            },
            {
              name: "alignContent",
              options: [
                "flex-start",
                "flex-end",
                "center",
                "space-between",
                "space-around",
                "stretch",
              ],
              tooltip: "Aligns flex lines.",
            },
          ]
        : []),
    ];

    const displayNameCssProperties = useMemo(
      () => [
        {
          name: "fontSize",
          options: ["12px", "14px", "16px", "20px", "24px", "28px", "32px"],
          tooltip: "Sets display name text size.",
        },
        {
          name: "fontFamily",
          options: [
            "Arial, sans-serif",
            "Helvetica, sans-serif",
            "Georgia, serif",
            "Times New Roman, serif",
            "Verdana, sans-serif",
            "Roboto, sans-serif",
            "Lato, sans-serif",
          ],
          tooltip: "Chooses display name font.",
        },
        {
          name: "fontWeight",
          options: [
            "normal",
            "bold",
            "bolder",
            "lighter",
            "100",
            "200",
            "300",
            "400",
            "500",
            "600",
            "700",
            "800",
            "900",
          ],
          tooltip: "Sets display name thickness.",
        },
        { name: "color", type: "color", tooltip: "Sets display name color." },
        {
          name: "textTransform",
          options: ["none", "uppercase", "lowercase", "capitalize"],
          tooltip: "Changes display name case.",
        },
        {
          name: "textAlign",
          options: ["left", "center", "right", "justify"],
          tooltip: "Aligns display name text.",
        },
        {
          name: "marginTop",
          options: spacingOptions,
          tooltip: "Sets display name top outer space.",
        },
        {
          name: "marginRight",
          options: spacingOptions,
          tooltip: "Sets display name right outer space.",
        },
        {
          name: "marginBottom",
          options: spacingOptions,
          tooltip: "Sets display name bottom outer space.",
        },
        {
          name: "marginLeft",
          options: spacingOptions,
          tooltip: "Sets display name left outer space.",
        },
        {
          name: "paddingTop",
          options: spacingOptions,
          tooltip: "Sets display name top inner space.",
        },
        {
          name: "paddingRight",
          options: spacingOptions,
          tooltip: "Sets display name right inner space.",
        },
        {
          name: "paddingBottom",
          options: spacingOptions,
          tooltip: "Sets display name bottom inner space.",
        },
        {
          name: "paddingLeft",
          options: spacingOptions,
          tooltip: "Sets display name left inner space.",
        },
        {
          name: "letterSpacing",
          options: ["normal", "0px", "1px", "2px", "3px", "4px"],
          tooltip: "Adjusts display name letter spacing.",
        },
        {
          name: "lineHeight",
          options: ["normal", "1", "1.2", "1.5", "1.8", "2"],
          tooltip: "Sets display name line spacing.",
        },
      ],
      []
    );

    const displayNameContainerCssProperties = useMemo(
      () => [
        {
          name: "backgroundColor",
          type: "color",
          tooltip: "Sets display name container background.",
        },
        {
          name: "color",
          type: "color",
          tooltip: "Sets display name container text color.",
        },
        {
          name: "marginTop",
          options: spacingOptions,
          tooltip: "Sets display name container top outer space.",
        },
        {
          name: "marginRight",
          options: spacingOptions,
          tooltip: "Sets display name container right outer space.",
        },
        {
          name: "marginBottom",
          options: spacingOptions,
          tooltip: "Sets display name container bottom outer space.",
        },
        {
          name: "marginLeft",
          options: spacingOptions,
          tooltip: "Sets display name container left outer space.",
        },
        {
          name: "paddingTop",
          options: spacingOptions,
          tooltip: "Sets display name container top inner space.",
        },
        {
          name: "paddingRight",
          options: spacingOptions,
          tooltip: "Sets display name container right inner space.",
        },
        {
          name: "paddingBottom",
          options: spacingOptions,
          tooltip: "Sets display name container bottom inner space.",
        },
        {
          name: "paddingLeft",
          options: spacingOptions,
          tooltip: "Sets display name container left inner space.",
        },
        {
          name: "borderWidth",
          options: ["0px", "1px", "2px", "3px", "4px"],
          tooltip: "Sets display name container border thickness.",
        },
        {
          name: "borderStyle",
          options: ["none", "solid", "dashed", "dotted", "double"],
          tooltip: "Chooses display name container border style.",
        },
        {
          name: "borderColor",
          type: "color",
          tooltip: "Sets display name container border color.",
        },
        {
          name: "borderRadius",
          options: ["0px", "4px", "8px", "12px", "16px", "24px", "32px"],
          tooltip: "Rounds display name container corners.",
        },
        {
          name: "boxShadow",
          options: [
            "none",
            "0 2px 4px rgba(0,0,0,0.1)",
            "0 4px 16px rgba(0,0,0,0.2)",
            "0 8px 16px rgba(0,0,0,0.3)",
          ],
          tooltip: "Adds display name container shadow.",
        },
        {
          name: "opacity",
          options: ["0.5", "0.6", "0.7", "0.8", "0.9", "1"],
          tooltip: "Sets display name container transparency.",
        },
        {
          name: "display",
          options: ["block", "flex", "grid", "inline", "inline-block"],
          tooltip: "Sets display name container display type.",
        },
        {
          name: "justifyContent",
          options: [
            "flex-start",
            "flex-end",
            "center",
            "space-between",
            "space-around",
            "space-evenly",
          ],
          tooltip: "Sets horizontal alignment for display name container.",
        },
        {
          name: "alignItems",
          options: ["flex-start", "flex-end", "center", "baseline", "stretch"],
          tooltip: "Sets vertical alignment for display name container.",
        },
        {
          name: "flexWrap",
          options: ["nowrap", "wrap", "wrap-reverse"],
          tooltip: "Controls flex wrap for display name container.",
        },
        {
          name: "gap",
          options: ["0px", "8px", "16px", "24px", "32px"],
          tooltip: "Sets gap between elements in display name container.",
        },
      ],
      []
    );

    const outerCssProperties = useMemo(
      () => [
        {
          name: "backgroundColor",
          type: "color",
          tooltip: "Sets outer container background.",
        },
        {
          name: "paddingTop",
          options: spacingOptions,
          tooltip: "Sets outer container top inner space.",
        },
        {
          name: "paddingRight",
          options: spacingOptions,
          tooltip: "Sets outer container right inner space.",
        },
        {
          name: "paddingBottom",
          options: spacingOptions,
          tooltip: "Sets outer container bottom inner space.",
        },
        {
          name: "paddingLeft",
          options: spacingOptions,
          tooltip: "Sets outer container left inner space.",
        },
        {
          name: "marginTop",
          options: spacingOptions,
          tooltip: "Sets outer container top outer space.",
        },
        {
          name: "marginRight",
          options: spacingOptions,
          tooltip: "Sets outer container right outer space.",
        },
        {
          name: "marginBottom",
          options: spacingOptions,
          tooltip: "Sets outer container bottom outer space.",
        },
        {
          name: "marginLeft",
          options: spacingOptions,
          tooltip: "Sets outer container left outer space.",
        },
        {
          name: "borderRadius",
          options: ["0px", "4px", "8px", "12px", "16px", "24px", "32px"],
          tooltip: "Rounds outer container corners.",
        },
        {
          name: "boxShadow",
          options: [
            "none",
            "0 2px 4px rgba(0,0,0,0.1)",
            "0 4px 8px rgba(0,0,0,0.2)",
            "0 8px 16px rgba(0,0,0,0.3)",
          ],
          tooltip: "Adds outer container shadow.",
        },
      ],
      []
    );

    const parentCssProperties = useMemo(
      () => [
        {
          name: "backgroundColor",
          type: "color",
          tooltip: "Sets parent container background.",
        },
        {
          name: "gap",
          options: ["0px", "8px", "16px", "24px", "32px", "48px"],
          tooltip: "Sets space between children.",
        },
        {
          name: "paddingTop",
          options: spacingOptions,
          tooltip: "Sets parent container top inner space.",
        },
        {
          name: "paddingRight",
          options: spacingOptions,
          tooltip: "Sets parent container right inner space.",
        },
        {
          name: "paddingBottom",
          options: spacingOptions,
          tooltip: "Sets parent container bottom inner space.",
        },
        {
          name: "paddingLeft",
          options: spacingOptions,
          tooltip: "Sets parent container left inner space.",
        },
        {
          name: "marginTop",
          options: spacingOptions,
          tooltip: "Sets parent container top outer space.",
        },
        {
          name: "marginRight",
          options: spacingOptions,
          tooltip: "Sets parent container right outer space.",
        },
        {
          name: "marginBottom",
          options: spacingOptions,
          tooltip: "Sets parent container bottom outer space.",
        },
        {
          name: "marginLeft",
          options: spacingOptions,
          tooltip: "Sets parent container left outer space.",
        },
        {
          name: "borderWidth",
          options: ["0px", "1px", "2px", "3px", "4px"],
          tooltip: "Sets parent container border thickness.",
        },
        {
          name: "borderStyle",
          options: ["none", "solid", "dashed", "dotted", "double"],
          tooltip: "Chooses parent container border style.",
        },
        {
          name: "borderColor",
          type: "color",
          tooltip: "Sets parent container border color.",
        },
        {
          name: "borderRadius",
          options: ["0px", "4px", "8px", "12px", "16px", "24px"],
          tooltip: "Rounds parent container corners.",
        },
        {
          name: "boxShadow",
          options: [
            "none",
            "0 2px 4px rgba(0,0,0,0.1)",
            "0 4px 8px rgba(0,0,0,0.2)",
            "0 8px 16px rgba(0,0,0,0.3)",
          ],
          tooltip: "Adds parent container shadow.",
        },
        {
          name: "width",
          options: ["auto", "100%", "80%", "60%", "40%", "20%"],
          tooltip: "Sets parent container width.",
        },
        {
          name: "maxWidth",
          options: ["none", "1200px", "1000px", "800px", "600px"],
          tooltip: "Sets parent container max width.",
        },
        {
          name: "minHeight",
          options: ["auto", "200px", "400px", "600px", "800px"],
          tooltip: "Sets parent container min height.",
        },
        {
          name: "overflow",
          options: ["visible", "hidden", "scroll", "auto"],
          tooltip: "Controls parent container overflow.",
        },
      ],
      []
    );

    const mergedSectionCssProperties = useMemo(
      () => [
        {
          name: "backgroundColor",
          type: "color",
          tooltip: "Sets merged section background.",
        },
        {
          name: "gap",
          options: ["0px", "8px", "16px", "24px", "32px"],
          tooltip: "Sets space between children.",
        },
        {
          name: "paddingTop",
          options: spacingOptions,
          tooltip: "Sets merged section top inner space.",
        },
        {
          name: "paddingRight",
          options: spacingOptions,
          tooltip: "Sets merged section right inner space.",
        },
        {
          name: "paddingBottom",
          options: spacingOptions,
          tooltip: "Sets merged section bottom inner space.",
        },
        {
          name: "paddingLeft",
          options: spacingOptions,
          tooltip: "Sets merged section left inner space.",
        },
        {
          name: "marginTop",
          options: spacingOptions,
          tooltip: "Sets merged section top outer space.",
        },
        {
          name: "marginRight",
          options: spacingOptions,
          tooltip: "Sets merged section right outer space.",
        },
        {
          name: "marginBottom",
          options: spacingOptions,
          tooltip: "Sets merged section bottom outer space.",
        },
        {
          name: "marginLeft",
          options: spacingOptions,
          tooltip: "Sets merged section left outer space.",
        },
        {
          name: "borderWidth",
          options: ["0px", "1px", "2px", "3px", "4px"],
          tooltip: "Sets merged section border thickness.",
        },
        {
          name: "borderStyle",
          options: ["none", "solid", "dashed", "dotted", "double"],
          tooltip: "Chooses merged section border style.",
        },
        {
          name: "borderColor",
          type: "color",
          tooltip: "Sets merged section border color.",
        },
        {
          name: "borderRadius",
          options: ["0px", "4px", "8px", "12px", "16px", "24px"],
          tooltip: "Rounds merged section corners.",
        },
        {
          name: "boxShadow",
          options: [
            "none",
            "0 2px 4px rgba(0,0,0,0.1)",
            "0 4px 8px rgba(0,0,0,0.2)",
            "0 8px 16px rgba(0,0,0,0.3)",
          ],
          tooltip: "Adds merged section shadow.",
        },
        {
          name: "opacity",
          options: ["0.5", "0.6", "0.7", "0.8", "0.9", "1"],
          tooltip: "Sets merged section transparency.",
        },
      ],
      []
    );

    // Function to get visible properties for a section
    const getVisibleProperties = (
      section,
      properties,
      cssStore = {},
      childIndex = null
    ) => {
      const defaultProps = defaultVisibleProperties[section] || [];
      const addedProps =
        childIndex !== null
          ? addedProperties.children[`${childIndex}-${section}`] || []
          : addedProperties[section] || [];
      const deletedProps =
        childIndex !== null
          ? deletedProperties.children[`${childIndex}-${section}`] || []
          : deletedProperties[section] || [];
      const cssProps =
        cssStore && typeof cssStore === "object"
          ? Object.keys(cssStore).filter(
              (prop) =>
                cssStore[prop] !== undefined &&
                !defaultProps.includes(prop) &&
                !addedProps.includes(prop)
            )
          : [];
      // Combine properties in order: default, added, then CSS store extras, excluding deleted
      const allPropNames = [...defaultProps, ...addedProps, ...cssProps].filter(
        (prop) => !deletedProps.includes(prop)
      );
      return allPropNames
        .map((name) => properties.find((prop) => prop.name === name))
        .filter(Boolean);
    };

    // Function to handle adding new properties
    const handleAddProperty = (
      section,
      propertyName,
      cssKey,
      changeHandler,
      cssStore = {},
      childIndex = null
    ) => {
      if (!propertyName) return;

      // Update state for added properties, appending to the end
      setAddedProperties((prev) => {
        if (childIndex !== null) {
          const childKey = `${childIndex}-${section}`;
          return {
            ...prev,
            children: {
              ...prev.children,
              [childKey]: [...(prev.children[childKey] || []), propertyName],
            },
          };
        }
        return {
          ...prev,
          [section]: [...prev[section], propertyName],
        };
      });

      // Remove from deletedProperties if it was previously deleted
      setDeletedProperties((prev) => {
        if (childIndex !== null) {
          const childKey = `${childIndex}-${section}`;
          return {
            ...prev,
            children: {
              ...prev.children,
              [childKey]: (prev.children[childKey] || []).filter(
                (prop) => prop !== propertyName
              ),
            },
          };
        }
        return {
          ...prev,
          [section]: prev[section].filter((prop) => prop !== propertyName),
        };
      });

      // Close the menu
      setAnchorEl((prev) => ({
        ...prev,
        [childIndex !== null ? `${childIndex}-${section}` : section]: null,
      }));

      // If the property isn't already in the CSS store, set its default value
      if (!cssStore[propertyName]) {
        const defaultValue = defaultPropertyValues[propertyName] || "";
        changeHandler(propertyName, defaultValue, cssKey, childIndex);
      }
    };

    // Function to handle deleting properties
    const handleDeleteProperty = (
      section,
      propertyName,
      cssKey,
      changeHandler,
      childIndex = null
    ) => {
      // Remove from addedProperties if it exists
      setAddedProperties((prev) => {
        if (childIndex !== null) {
          const childKey = `${childIndex}-${section}`;
          return {
            ...prev,
            children: {
              ...prev.children,
              [childKey]: (prev.children[childKey] || []).filter(
                (prop) => prop !== propertyName
              ),
            },
          };
        }
        return {
          ...prev,
          [section]: prev[section].filter((prop) => prop !== propertyName),
        };
      });

      // Add to deletedProperties if it's a default property
      const defaultProps = defaultVisibleProperties[section] || [];
      if (defaultProps.includes(propertyName)) {
        setDeletedProperties((prev) => {
          if (childIndex !== null) {
            const childKey = `${childIndex}-${section}`;
            return {
              ...prev,
              children: {
                ...prev.children,
                [childKey]: [...(prev.children[childKey] || []), propertyName],
              },
            };
          }
          return {
            ...prev,
            [section]: [...prev[section], propertyName],
          };
        });
      }

      // Remove the property from the CSS store by setting it to undefined
      changeHandler(propertyName, undefined, cssKey, childIndex);
    };

    const handleCssChange = (
      property,
      value,
      cssKey = selectedBlock?.type || "",
      childIndex = null
    ) => {
      if (
        !selectedBlock ||
        !property ||
        typeof updateBlock !== "function" ||
        (childIndex !== null && !Number.isInteger(childIndex))
      )
        return;

      // Append "px" for spacing properties
      const isSpacingProp =
        property.includes("margin") ||
        property.includes("padding") ||
        property === "gap";
      const finalValue =
        isSpacingProp && value !== "" && value !== undefined
          ? `${value}px`
          : value;

      const updatedBlock = { ...selectedBlock };
      if (childIndex !== null) {
        updatedBlock.children = Array.isArray(updatedBlock.children)
          ? [...updatedBlock.children]
          : [];
        updatedBlock.children[childIndex] = {
          ...(updatedBlock.children[childIndex] || {}),
          css: {
            ...(updatedBlock.children[childIndex]?.css || {}),
            [cssKey]: {
              ...(updatedBlock.children[childIndex]?.css?.[cssKey] || {}),
              [property]: finalValue === undefined ? undefined : finalValue,
            },
          },
        };
      } else {
        updatedBlock.css = {
          ...(updatedBlock.css || {}),
          [cssKey]: {
            ...(updatedBlock.css?.[cssKey] || {}),
            [property]: finalValue === undefined ? undefined : finalValue,
          },
        };
      }
      updateBlock(updatedBlock);
    };

    const handleAlignmentChange = (
      value,
      cssKey,
      cssStoreKey,
      childIndex = null
    ) => {
      if (
        !selectedBlock ||
        !value ||
        typeof updateBlock !== "function" ||
        (childIndex !== null && !Number.isInteger(childIndex))
      )
        return;

      const alignment = alignmentOptions.find((opt) => opt.value === value);
      if (!alignment) return;

      const updatedBlock = { ...selectedBlock };
      if (childIndex !== null) {
        updatedBlock.children = Array.isArray(updatedBlock.children)
          ? [...updatedBlock.children]
          : [];
        updatedBlock.children[childIndex] = {
          ...(updatedBlock.children[childIndex] || {}),
          [cssStoreKey]: {
            ...(updatedBlock.children[childIndex]?.[cssStoreKey] || {}),
            ...alignment.css,
          },
        };
      } else {
        if (cssStoreKey === "mergedCss") {
          updatedBlock.mergedCss = {
            ...(updatedBlock.mergedCss || {}),
            ...alignment.css,
          };
        } else if (cssStoreKey === "outerCss") {
          updatedBlock.outerCss = {
            ...(updatedBlock.outerCss || {}),
            ...alignment.css,
          };
        } else if (cssStoreKey === "css" && cssKey) {
          updatedBlock.css = {
            ...(updatedBlock.css || {}),
            [cssKey]: {
              ...(updatedBlock.css?.[cssKey] || {}),
              ...alignment.css,
            },
          };
        } else if (cssStoreKey === "displayNameContainer") {
          updatedBlock.css = {
            ...(updatedBlock.css || {}),
            displayNameContainer: {
              ...(updatedBlock.css?.displayNameContainer || {}),
              ...alignment.css,
            },
          };
        }
      }
      updateBlock(updatedBlock);
    };

    const handleCardWidthTypeChange = (value, childIndex = null) => {
    setcardWidthType(value)
    };

    const handleParentAlignmentChange = (value) => {
      if (!value || typeof updateParentCss !== "function") return;

      const alignment = alignmentOptions.find((opt) => opt.value === value);
      if (!alignment) return;

      updateParentCss({
        ...(parentCss || {}),
        ...alignment.css,
      });
    };

    const handleMergedSectionCssChange = (property, value) => {
      if (!selectedBlock || !property || typeof updateBlock !== "function")
        return;

      // Append "px" for spacing properties
      const isSpacingProp =
        property.includes("margin") ||
        property.includes("padding") ||
        property === "gap";
      const finalValue =
        isSpacingProp && value !== "" && value !== undefined
          ? `${value}px`
          : value;

      const updatedBlock = { ...selectedBlock };
      updatedBlock.mergedCss = {
        ...(updatedBlock.mergedCss || {}),
        [property]: finalValue === undefined ? undefined : finalValue,
      };
      updateBlock(updatedBlock);
    };

    const handleOuterCssChange = (property, value, childIndex = null) => {
      if (
        !selectedBlock ||
        !property ||
        typeof updateBlock !== "function" ||
        (childIndex !== null && !Number.isInteger(childIndex))
      )
        return;

      // Append "px" for spacing properties
      const isSpacingProp =
        property.includes("margin") ||
        property.includes("padding") ||
        property === "gap";
      const finalValue =
        isSpacingProp && value !== "" && value !== undefined
          ? `${value}px`
          : value;

      const updatedBlock = { ...selectedBlock };
      if (childIndex !== null) {
        updatedBlock.children = Array.isArray(updatedBlock.children)
          ? [...updatedBlock.children]
          : [];
        updatedBlock.children[childIndex] = {
          ...(updatedBlock.children[childIndex] || {}),
          outerCss: {
            ...(updatedBlock.children[childIndex]?.outerCss || {}),
            [property]: finalValue === undefined ? undefined : finalValue,
          },
        };
      } else {
        updatedBlock.outerCss = {
          ...(updatedBlock.outerCss || {}),
          [property]: finalValue === undefined ? undefined : finalValue,
        };
      }
      updateBlock(updatedBlock);
    };

    const handleParentCssChange = (property, value) => {
      if (!property || typeof updateParentCss !== "function") return;

      // Append "px" for spacing properties
      const isSpacingProp =
        property.includes("margin") ||
        property.includes("padding") ||
        property === "gap";
      const finalValue =
        isSpacingProp && value !== "" && value !== undefined
          ? `${value}px`
          : value;

      updateParentCss({
        ...(parentCss || {}),
        [property]: finalValue === undefined ? undefined : finalValue,
      });
    };

    const renderInput = (
      prop,
      cssKey,
      changeHandler,
      cssStore = {},
      childIndex = null,
      section
    ) => {
      if (!prop?.name) return null;

      const isSpacingProp =
        prop.name.includes("margin") ||
        prop.name.includes("padding") ||
        prop.name === "gap";
      // Extract numeric value from stored CSS (remove "px")
      const value = cssStore[prop.name] || "";
      const displayValue = isSpacingProp ? value.replace("px", "") : value;

      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {prop.type === "color" ? (
            <input
              type="color"
              value={value || (prop.name === "color" ? "#000000" : "#ffffff")}
              onChange={(e) =>
                changeHandler(prop.name, e.target.value, cssKey, childIndex)
              }
              style={{ width: "100%" }}
            />
          ) : (
            <Box sx={{ display: "flex", gap: "8px", width: "100%" }}>
              {isSpacingProp ? (
                <TextField
                  label={formatPropertyName(prop.name)}
                  size="small"
                  value={displayValue}
                  onChange={(e) =>
                    changeHandler(prop.name, e.target.value, cssKey, childIndex)
                  }
                  type="number"
                  InputProps={{
                    endAdornment: <Box sx={{ color: "white", pr: 1 }}>px</Box>,
                  }}
                  sx={{ flex: 1 }}
                />
              ) : (
                <Select
                  value={prop.options.includes(value) ? value : ""}
                  onChange={(e) =>
                    changeHandler(prop.name, e.target.value, cssKey, childIndex)
                  }
                  sx={{ flex: 1, color: "white", // text color
    svg: {
      color: "white", // dropdown arrow color
    },
    '.MuiSelect-icon': {
      color: 'white', // this targets the arrow specifically
    }, }}
                >
                  <MenuItem value="">None</MenuItem>
                  {prop.options?.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {prop.tooltip && (
              <Tooltip title={prop.tooltip} placement="right">
                <IconButton size="small" sx={{ color: "#E0E0E0" }}>
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Delete Property" placement="right">
              <IconButton
                size="small"
                sx={{ color: "#E0E0E0" }}
                onClick={() =>
                  handleDeleteProperty(
                    section,
                    prop.name,
                    cssKey,
                    changeHandler,
                    childIndex
                  )
                }
              >
                <IoMdTrash size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      );
    };

    const renderAlignmentInput = (
      cssKey,
      changeHandler,
      cssStore = {},
      cssStoreKey,
      childIndex = null
    ) => {
      const alignmentKeys = [
        "display",
        "justifyContent",
        "alignItems",
        "flexDirection",
        "gridTemplateColumns",
        "gap",
        "flexWrap",
      ];

      const currentAlignment =
        alignmentOptions.find((opt) => {
          const css = opt.css;
          const relevantKeys = Object.keys(css).filter(
            (key) => css[key] !== undefined && alignmentKeys.includes(key)
          );
          return (
            relevantKeys.length > 0 &&
            relevantKeys.every((key) => cssStore[key] === css[key])
          );
        })?.value || "default";

      const currentTooltip =
        alignmentOptions.find((opt) => opt.value === currentAlignment)
          ?.tooltip || "";

      return (
        <FormControl
          sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "150px", mb: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FormLabel sx={{ color: "#E0E0E0" }}>Alignment</FormLabel>
            <Tooltip title={currentTooltip} placement="right">
              <IconButton size="small" sx={{ color: "#E0E0E0" }}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Select
            value={currentAlignment}
            onChange={(e) =>
              changeHandler(e.target.value, cssKey, cssStoreKey, childIndex)
            }
            sx={{ width: "100%", color: "white", // text color
    svg: {
      color: "white", // dropdown arrow color
    },
    '.MuiSelect-icon': {
      color: 'white', // this targets the arrow specifically
    }, }}
          >
            {alignmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}
              
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    };

    const renderCardWidthTypeInput = (cssStore = {}, childIndex = null) => {
      const styleKeys = ["width", "display", "gridTemplateColumns", "gap"];
      const currentStyle =
        cardWidthTypeOptions.find((opt) => {
          const css = opt.css;
          const relevantKeys = Object.keys(css).filter(
            (key) => css[key] !== undefined && styleKeys.includes(key)
          );
          return (
            relevantKeys.length > 0 &&
            relevantKeys.every((key) => cssStore[key] === css[key])
          );
        })?.value || "full-width-card";

      const currentTooltip =
        cardWidthTypeOptions.find((opt) => opt.value === currentStyle)?.label || "Select card width type";

      return (
        <FormControl
          sx={{ flex: "1 1 calc(50% - 8px)", minWidth: "150px", mb: 2 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FormLabel sx={{ color: "#E0E0E0" }}>Card Width Type</FormLabel>
            <Tooltip title={currentTooltip} placement="right">
              <IconButton size="small" sx={{ color: "#E0E0E0" }}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Select
            value={cardWidthType}
            onChange={(e) => handleCardWidthTypeChange(e.target.value, childIndex)}
            sx={{ width: "100%", color: "white", // text color
    svg: {
      color: "white", // dropdown arrow color
    },
    '.MuiSelect-icon': {
      color: 'white', // this targets the arrow specifically
    }, }}
          >
            {cardWidthTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    };

    const renderAddMoreButton = (
      section,
      properties,
      cssKey,
      changeHandler,
      cssStore = {},
      childIndex = null
    ) => {
      const visibleProps = getVisibleProperties(
        section,
        properties,
        cssStore,
        childIndex
      );
      const availableProps = properties
        .filter(
          (prop) =>
            !visibleProps.some((visibleProp) => visibleProp.name === prop.name)
        )
        .map((prop) => ({
          value: prop.name,
          label: formatPropertyName(prop.name),
        }));

      if (availableProps.length === 0) return null;

      const menuKey =
        childIndex !== null ? `${childIndex}-${section}` : section;
      const currentAnchorEl = anchorEl[menuKey];

      return (
        <Box sx={{ mb: 2, width: "100%" }}>
          <SuperAppButton
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={(e) =>
              setAnchorEl((prev) => ({
                ...prev,
                [menuKey]: e.currentTarget,
              }))
            }
            sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
          >
            Add More
          </SuperAppButton>
          <Menu
            anchorEl={currentAnchorEl}
            open={Boolean(currentAnchorEl)}
            onClose={() =>
              setAnchorEl((prev) => ({
                ...prev,
                [menuKey]: null,
              }))
            }
            PaperProps={{ sx: { bgcolor: "#202345", color: "#fff" } }}
          >
            {availableProps.map(({ value, label }) => (
              <MenuItem
                key={value}
                onClick={() =>
                  handleAddProperty(
                    section,
                    value,
                    cssKey,
                    changeHandler,
                    cssStore,
                    childIndex
                  )
                }
                sx={{
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    };

    const renderChildStyles = (child = {}, index) => {
      const childType = child.type || "";
      const cssProperties = getCssProperties(childType);
      const cssKeys =
        child.css && typeof child.css === "object"
          ? Object.keys(child.css)
          : [];

      return (
        <Accordion
          key={`child-${child.id || index}`}
          sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Block {index + 1}: {child.display_name || "Unnamed"} (
              {childType || "Unknown"})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ backgroundColor: "#202345" }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {/* Wrapper Styles Accordion */}
              <Accordion sx={{ backgroundColor: "#202345", color: "#E0E0E0" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Wrapper Styles
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {renderAlignmentInput(
                      undefined,
                      handleAlignmentChange,
                      child.outerCss,
                      "outerCss",
                      index
                    )}
                    {getVisibleProperties(
                      "outer",
                      outerCssProperties,
                      child.outerCss,
                      index
                    ).map((prop) => (
                      <FormControl
                        key={`outer-${prop.name}`}
                        sx={{
                          flex: "1 1 calc(50% - 8px)",
                          minWidth: "150px",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormLabel sx={{ color: "#E0E0E0" }}>
                            {formatPropertyName(prop.name)}
                          </FormLabel>
                        </Box>
                        {renderInput(
                          prop,
                          undefined,
                          handleOuterCssChange,
                          child.outerCss,
                          index,
                          "outer"
                        )}
                      </FormControl>
                    ))}
                    {renderAddMoreButton(
                      "outer",
                      outerCssProperties,
                      undefined,
                      handleOuterCssChange,
                      child.outerCss,
                      index
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Content Styles Accordion */}
              <Accordion sx={{ backgroundColor: "#202345", color: "#E0E0E0" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Content Styles
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {renderAlignmentInput(
                      childType,
                      handleAlignmentChange,
                      child.css?.[childType],
                      "css",
                      index
                    )}
                    {getVisibleProperties(
                      "block",
                      cssProperties,
                      child.css?.[childType],
                      index
                    ).map((prop) => (
                      <FormControl
                        key={`block-${prop.name}`}
                        sx={{
                          flex: "1 1 calc(50% - 8px)",
                          minWidth: "150px",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormLabel sx={{ color: "#E0E0E0" }}>
                            {formatPropertyName(prop.name)}
                          </FormLabel>
                        </Box>
                        {renderInput(
                          prop,
                          childType,
                          handleCssChange,
                          child.css?.[childType],
                          index,
                          "block"
                        )}
                      </FormControl>
                    ))}
                    {renderAddMoreButton(
                      "block",
                      cssProperties,
                      childType,
                      handleCssChange,
                      child.css?.[childType],
                      index
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Label Styles Accordion */}
              <Accordion sx={{ backgroundColor: "#202345", color: "#E0E0E0" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Label Styles
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {getVisibleProperties(
                      "displayName",
                      displayNameCssProperties,
                      child.css?.displayName,
                      index
                    ).map((prop) => (
                      <FormControl
                        key={`displayName-${prop.name}`}
                        sx={{
                          flex: "1 1 calc(50% - 8px)",
                          minWidth: "150px",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormLabel sx={{ color: "#E0E0E0" }}>
                            {formatPropertyName(prop.name)}
                          </FormLabel>
                        </Box>
                        {renderInput(
                          prop,
                          "displayName",
                          handleCssChange,
                          child.css?.displayName,
                          index,
                          "displayName"
                        )}
                      </FormControl>
                    ))}
                    {renderAddMoreButton(
                      "displayName",
                      displayNameCssProperties,
                      "displayName",
                      handleCssChange,
                      child.css?.displayName,
                      index
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Label Wrapper Styles Accordion */}
              <Accordion sx={{ backgroundColor: "#202345", color: "#E0E0E0" }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                    Label Wrapper Styles
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                    {renderAlignmentInput(
                      undefined,
                      handleAlignmentChange,
                      child.css?.displayNameContainer,
                      "displayNameContainer",
                      index
                    )}
                    {getVisibleProperties(
                      "displayNameContainer",
                      displayNameContainerCssProperties,
                      child.css?.displayNameContainer,
                      index
                    ).map((prop) => (
                      <FormControl
                        key={`displayNameContainer-${prop.name}`}
                        sx={{
                          flex: "1 1 calc(50% - 8px)",
                          minWidth: "150px",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FormLabel sx={{ color: "#E0E0E0" }}>
                            {formatPropertyName(prop.name)}
                          </FormLabel>
                        </Box>
                        {renderInput(
                          prop,
                          "displayNameContainer",
                          handleCssChange,
                          child.css?.displayNameContainer,
                          index,
                          "displayNameContainer"
                        )}
                      </FormControl>
                    ))}
                    {renderAddMoreButton(
                      "displayNameContainer",
                      displayNameContainerCssProperties,
                      "displayNameContainer",
                      handleCssChange,
                      child.css?.displayNameContainer,
                      index
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    };

    return (
      <Box sx={{ p: "16px", backgroundColor: "#1A1C37" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 2, color: "#E0E0E0" }}
        >
          Customize Your Card
        </Typography>
        <Box sx={{ height: "calc(100vh - 100px)", overflowY: "auto" }}>
          <Accordion
            sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                Card Styles
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ backgroundColor: "#202345" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
                {renderCardWidthTypeInput(selectedBlock?.outerCss)}
                {renderAlignmentInput(
                  undefined,
                  handleParentAlignmentChange,
                  parentCss,
                  "parentCss"
                )}
                {getVisibleProperties(
                  "parent",
                  parentCssProperties,
                  parentCss
                ).map((prop) => (
                  <FormControl
                    key={`parent-${prop.name}`}
                    sx={{
                      flex: "1 1 calc(50% - 8px)",
                      minWidth: "150px",
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <FormLabel sx={{ color: "#E0E0E0" }}>
                        {formatPropertyName(prop.name)}
                      </FormLabel>
                    </Box>
                    {renderInput(
                      prop,
                      undefined,
                      handleParentCssChange,
                      parentCss,
                      null,
                      "parent"
                    )}
                  </FormControl>
                ))}
                {renderAddMoreButton(
                  "parent",
                  parentCssProperties,
                  undefined,
                  handleParentCssChange,
                  parentCss
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          {!selectedBlock ? (
            <Typography variant="body2" sx={{ color: "#E0E0E0", mt: 2 }}>
              Select a block to edit its styles
            </Typography>
          ) : (
            <>
              {!selectedBlock?.isMerged && (
                <Accordion
                  sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      Wrapper Styles
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ backgroundColor: "#202345" }}>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                    >
                      {renderAlignmentInput(
                        undefined,
                        handleAlignmentChange,
                        selectedBlock.outerCss,
                        "outerCss"
                      )}
                      {getVisibleProperties(
                        "outer",
                        outerCssProperties,
                        selectedBlock.outerCss
                      ).map((prop) => (
                        <FormControl
                          key={`outer-main-${prop.name}`}
                          sx={{
                            flex: "1 1 calc(50% - 8px)",
                            minWidth: "150px",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <FormLabel sx={{ color: "#E0E0E0" }}>
                              {formatPropertyName(prop.name)}
                            </FormLabel>
                          </Box>
                          {renderInput(
                            prop,
                            undefined,
                            handleOuterCssChange,
                            selectedBlock.outerCss,
                            null,
                            "outer"
                          )}
                        </FormControl>
                      ))}
                      {renderAddMoreButton(
                        "outer",
                        outerCssProperties,
                        undefined,
                        handleOuterCssChange,
                        selectedBlock.outerCss
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
              {selectedBlock.isMerged && (
                <>
                  <Accordion
                    sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Section Styles
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "#202345" }}>
                      {Array.isArray(selectedBlock.children) &&
                        selectedBlock.children.map((child, index) =>
                          renderChildStyles(child, index)
                        )}
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                      >
                        {renderAlignmentInput(
                          undefined,
                          handleAlignmentChange,
                          selectedBlock.mergedCss,
                          "mergedCss"
                        )}
                        {getVisibleProperties(
                          "merged",
                          mergedSectionCssProperties,
                          selectedBlock.mergedCss
                        ).map((prop) => (
                          <FormControl
                            key={`merged-${prop.name}`}
                            sx={{
                              flex: "1 1 calc(50% - 8px)",
                              minWidth: "150px",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <FormLabel sx={{ color: "#E0E0E0" }}>
                                {formatPropertyName(prop.name)}
                              </FormLabel>
                            </Box>
                            {renderInput(
                              prop,
                              undefined,
                              handleMergedSectionCssChange,
                              selectedBlock.mergedCss,
                              null,
                              "merged"
                            )}
                          </FormControl>
                        ))}
                        {renderAddMoreButton(
                          "merged",
                          mergedSectionCssProperties,
                          undefined,
                          handleMergedSectionCssChange,
                          selectedBlock.mergedCss
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}

              {!selectedBlock?.isMerged && (
                <>
                  <Accordion
                    sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Content Styles
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "#202345" }}>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                      >
                        {renderAlignmentInput(
                          selectedBlock.type || "",
                          handleAlignmentChange,
                          selectedBlock.css?.[selectedBlock.type],
                          "css"
                        )}
                        {getVisibleProperties(
                          "block",
                          getCssProperties(selectedBlock.type || ""),
                          selectedBlock.css?.[selectedBlock.type]
                        ).map((prop) => (
                          <FormControl
                            key={`block-main-${prop.name}`}
                            sx={{
                              flex: "1 1 calc(50% - 8px)",
                              minWidth: "150px",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <FormLabel sx={{ color: "#E0E0E0" }}>
                                {formatPropertyName(prop.name)}
                              </FormLabel>
                            </Box>
                            {renderInput(
                              prop,
                              selectedBlock.type,
                              handleCssChange,
                              selectedBlock.css?.[selectedBlock.type],
                              null,
                              "block"
                            )}
                          </FormControl>
                        ))}
                        {renderAddMoreButton(
                          "block",
                          getCssProperties(selectedBlock.type || ""),
                          selectedBlock.type,
                          handleCssChange,
                          selectedBlock.css?.[selectedBlock.type]
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Label Styles
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "#202345" }}>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                      >
                        {getVisibleProperties(
                          "displayName",
                          displayNameCssProperties,
                          selectedBlock.css?.displayName
                        ).map((prop) => (
                          <FormControl
                            key={`displayName-main-${prop.name}`}
                            sx={{
                              flex: "1 1 calc(50% - 8px)",
                              minWidth: "150px",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <FormLabel sx={{ color: "#E0E0E0" }}>
                                {formatPropertyName(prop.name)}
                              </FormLabel>
                            </Box>
                            {renderInput(
                              prop,
                              "displayName",
                              handleCssChange,
                              selectedBlock.css?.displayName,
                              null,
                              "displayName"
                            )}
                          </FormControl>
                        ))}
                        {renderAddMoreButton(
                          "displayName",
                          displayNameCssProperties,
                          "displayName",
                          handleCssChange,
                          selectedBlock.css?.displayName
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    sx={{ backgroundColor: "#1A1C37", color: "#E0E0E0", mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#E0E0E0" }} />}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        Label Wrapper Styles
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ backgroundColor: "#202345" }}>
                      <Box
                        sx={{ display: "flex", flexWrap: "wrap", gap: "16px" }}
                      >
                        {renderAlignmentInput(
                          undefined,
                          handleAlignmentChange,
                          selectedBlock.css?.displayNameContainer,
                          "displayNameContainer"
                        )}
                        {getVisibleProperties(
                          "displayNameContainer",
                          displayNameContainerCssProperties,
                          selectedBlock.css?.displayNameContainer
                        ).map((prop) => (
                          <FormControl
                            key={`displayNameContainer-main-${prop.name}`}
                            sx={{
                              flex: "1 1 calc(50% - 8px)",
                              minWidth: "150px",
                              mb: 2,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <FormLabel sx={{ color: "#E0E0E0" }}>
                                {formatPropertyName(prop.name)}
                              </FormLabel>
                            </Box>
                            {renderInput(
                              prop,
                              "displayNameContainer",
                              handleCssChange,
                              selectedBlock.css?.displayNameContainer,
                              null,
                              "displayNameContainer"
                            )}
                          </FormControl>
                        ))}
                        {renderAddMoreButton(
                          "displayNameContainer",
                          displayNameContainerCssProperties,
                          "displayNameContainer",
                          handleCssChange,
                          selectedBlock.css?.displayNameContainer
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  }
);



const blockTypes = Object.keys(typeMatches)
  .concat(["section"])
  .map((type) => ({ type, defaultLabel: `A ${type} block.` }));

const CustomEditor = React.memo(
  ({
    initialJsons = [],
    initialJsons2 = [],
    handleSaveAllJson,
    primary_path = "",
    ui_name = "",
    apiData = {},
  }) => {
    const [blocks, setBlocks] = useState([]);
    console.log("🚀 ~ blocksblocks:", blocks)
    const [selectedBlock, setSelectedBlock] = useState(null);
    const [showEditorModal, setShowEditorModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showAddMoreModal, setShowAddMoreModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editBlock, setEditBlock] = useState(null);
    const [editBlockIndex, setEditBlockIndex] = useState(null);
    const [jsonData, setJsonData] = useState("");
    const [anchorElAdd, setAnchorElAdd] = useState(null);
    const [anchorElChangeType, setAnchorElChangeType] = useState(null);
    const [currentBlockIndex, setCurrentBlockIndex] = useState(null);
    const [dynamicInitialJsons2, setDynamicInitialJsons2] = useState([]);
    const [parentCss, setParentCss] = useState({
      display: "flex",
      flexDirection: "column",
      gap: "0",
      padding: "0",
    });
    const [layoutMode, setLayoutMode] = useState("horizontal");
    const [selectedBlocks, setSelectedBlocks] = useState([]);
    const [primaryPath, setPrimaryPath] = useState("");
    const [blockPathErrors, setBlockPathErrors] = useState({});
    const resizeRef = useRef(null);
    const [cardWidthType,setcardWidthType] = useState(null)

    const cardStyle = useMemo(
      () => ({
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        width: "100%",
        borderRadius: "10px",
        boxShadow: "0px 0px 10px #000000",
        boxSizing: "border-box",
      }),
      []
    );

    useEffect(() => {
      if (!Array.isArray(initialJsons)) return;
      try {
        const uiData = { blocks: [] };
        const updatedData = initialJsons;
        setBlocks(updatedData || []);
      } catch (error) {
        console.error("Error processing initialJsons:", error);
      }
    }, [initialJsons]);

    useEffect(() => {
      if (blocks.length && !resizeRef.current) {
        resizeRef.current = true;
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event("resize"));
          } catch (error) {
            console.warn("Error dispatching resize event:", error);
          }
        }, 100);
      }
    }, [blocks.length]);

    useEffect(() => {
      if (Array.isArray(initialJsons2)) {
        setDynamicInitialJsons2(initialJsons2);
      }
      if(primary_path){
        let newPrimaryPath = ""
        if(primary_path?.startsWith("main.data")){
          newPrimaryPath = primary_path
        }else{
          newPrimaryPath = "main.data."+primary_path
          console.log("🚀 ~ useEffect ~ newPrimaryPath:", newPrimaryPath)

          let values = getValueFromPath(newPrimaryPath,apiData)
          console.log("🚀 ~ useEffect ~ values:", values)
          if(!values){
newPrimaryPath= primary_path
}
}
console.log("🚀 ~ useEffect ~ newPrimaryPath:", newPrimaryPath)
        setPrimaryPath(newPrimaryPath);
      }
    }, []);

    // Validate block paths against primaryPath
  

  function customPathMaker(inputString, primaryPath = "", keyType = null) {
  try {
    // Parse input if it's a string
    let parsedInput = typeof inputString === "string" ? JSON.parse(inputString) : inputString;
    let finalOutput = "";

    parsedInput.forEach((item) => {
      let resolvedValue = "";

      if (item.type === "text" && item.text) {
        resolvedValue = item.text.trim();
        if (!resolvedValue) return;

        if (primaryPath && resolvedValue.startsWith(primaryPath)) {
          const pattern = new RegExp(`^${primaryPath.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\[\\d+\\])?\\.?`, "s");
          resolvedValue = resolvedValue.replace(pattern, "").replace(/^\[\d+\]\.?/, "").replace(/^\.+/, "");
        }

        // if (keyType === "image") {
        //   resolvedValue = resolvedValue.replace(/\[\d+\]/g, "[*]");
        // }

        finalOutput += resolvedValue;

      } else if (item.type === "mention" && item.attrs && item.attrs.id) {
        resolvedValue = item.attrs.id;

        if (primaryPath && resolvedValue.startsWith(primaryPath)) {
          const pattern = new RegExp(`^${primaryPath.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}(\\[\\d+\\])?\\.?`, "s");
          resolvedValue = resolvedValue.replace(pattern, "").replace(/^\[\d+\]\.?/, "").replace(/^\.+/, "");
        } else if (resolvedValue === primaryPath) {
          resolvedValue = "";
        }

        if (keyType === "image") {
          resolvedValue = resolvedValue.replace(/\[\d+\]/g, "[*]");
        }

        finalOutput += resolvedValue;
      }
    });

   
    return finalOutput || "No valid data processed";

  } catch (error) {
    
    return inputString;
  }
}

    const validateBlockPaths = useCallback(() => {
      function getValueFromPath(path, obj,primary_path) {
        let newPath = path
         if (path.includes("[*]")) {
      newPath = newPath.replace(/\[\*\]/g, "[0]");
    }
    const parts = newPath.replace(/\[(\d+)\]/g, '.$1').split('.');
    
    
    return parts.reduce((acc, part) => {
      if (acc == null) {
        
        return undefined;
      }
      if (Array.isArray(acc) && /^\d+$/.test(part)) {
        const index = parseInt(part, 10);
        if (index < acc.length) {
       
          return acc[index];
        } else {
       
          return undefined;
        }
      }

      return acc[part];
    }, obj);
  }
      if (!primaryPath || !apiData) return;
      const errors = {};
      blocks.forEach((block, index) => {
        const blockPath = block.path || ''; // Assuming each block has a path property
        let newPath = customPathMaker(blockPath,primaryPath)
        
        let primaryPathData = getValueFromPath(primaryPath,apiData)
        
        if(Array.isArray(primaryPathData)){
         let check = getValueFromPath(newPath,primaryPathData[0]||{})
         if(!check){
          
          errors[block.id] = `Path "${newPath}" does not exists in primary path "${primaryPath}"`;
         }
        }else if (typeof(primaryPathData)==="object"){
           let check = getValueFromPath(newPath,primaryPathData||{})
         if(!check){
          
          errors[block.id] = `Path "${newPath}" does not exists in primary path "${primaryPath}"`;
         }
        }
        // if (blockPath && !blockPath.startsWith(primaryPath)) {
        //   errors[block.id] = `Block path "${blockPath}" does not match primary path "${primaryPath}"`;
        // }
      });
      setBlockPathErrors(errors);
    }, [blocks, primaryPath, apiData]);

    useEffect(() => {
      validateBlockPaths();
    }, [primaryPath, blocks, validateBlockPaths]);

    const addMoreBlocks = useCallback((selectedJsons = []) => {
      if (!Array.isArray(selectedJsons)) return;
      try {
        const uiData = { blocks: [] };
        const updatedData = editUiDataWithInitialJsons(uiData, selectedJsons);
        const newBlocks = updatedData.blocks || [];
        setBlocks((prev) => [...prev, ...newBlocks]);
      } catch (error) {
        console.error("Error adding more blocks:", error);
      }
    }, []);

    const updateBlock = useCallback((index, updatedBlock) => {
      if (!isFinite(index) || index < 0 || !updatedBlock) return;
      try {
        setBlocks((prev) =>
          prev.map((block, i) => (i === index ? updatedBlock : block))
        );
        setSelectedBlock(updatedBlock);
      } catch (error) {
        console.error("Error updating block:", error);
      }
    }, []);

    const handleAddNewBlock = useCallback((blockType) => {
      if (!blockType || (!typeMatches[blockType] && blockType !== "section"))
        return;
      try {
        const newBlock = {
          id: `block-${uuidv4()}`,
          type: blockType,
          display_name: `New ${blockType}`,
          path: primaryPath, // Set block path to current primaryPath
          data: { elements: [] },
          css: {
            [blockType]: { ...(defaultCss[blockType] || {}) },
            displayName: { ...(defaultCss.displayName || {}) },
            displayNameContainer: {
              ...(defaultCss.displayNameContainer || {}),
            },
          },
          outerCss: { ...(defaultCss.outer || {}) },
          default_fields: {},
          is_default_value: false,
          add_display_name: true,
        };

        switch (blockType) {
          case "header":
          case "paragraph":
          case "quote":
            newBlock.data.elements.push({ field: "text", value: "" });
            newBlock.data.elements.push({
              field: "display_name",
              value: `New ${blockType}`,
            });
            break;
          case "button":
            newBlock.data.elements.push({ field: "text", value: "Click me" });
            newBlock.data.elements.push({ field: "url", value: "" });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Button",
            });
            break;
          case "list":
            newBlock.data.elements.push({ field: "items", value: [] });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New List",
            });
            break;
          case "image":
            newBlock.data.elements.push({ field: "files", value: [] });
            newBlock.data.elements.push({ field: "caption", value: "" });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Image",
            });
            break;
          case "form":
            newBlock.data.elements.push({ field: "title", value: "New Form" });
            newBlock.data.elements.push({ field: "fields", value: [] });
            newBlock.data.elements.push({
              field: "submitText",
              value: "Submit",
            });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Form",
            });
            break;
          case "card":
            newBlock.data.elements.push({ field: "title", value: "" });
            newBlock.data.elements.push({ field: "description", value: "" });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Card",
            });
            break;
          case "map":
            newBlock.data.elements.push({ field: "lat", value: null });
            newBlock.data.elements.push({ field: "lng", value: null });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Map",
            });
            break;
          case "table":
            newBlock.data.elements.push({ field: "headers", value: [] });
            newBlock.data.elements.push({ field: "rows", value: [] });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Table",
            });
            break;
          case "faq":
            newBlock.data.elements.push({ field: "items", value: [] });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New FAQ",
            });
            break;
          case "section":
            newBlock.data.elements.push({ field: "children", value: [] });
            newBlock.data.elements.push({
              field: "display_name",
              value: "New Section",
            });
            newBlock.css.section = {
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "16px",
            };
            break;
          default:
            throw new Error(`Invalid block type: ${blockType}`);
        }

        setBlocks((prev) => [...prev, newBlock]);
        setAnchorElAdd(null);
      } catch (error) {
        console.error("Error adding new block:", error);
      }
    }, [primaryPath]);

    const handleChangeBlockType = useCallback(
      (index, newType) => {
        if (
          !isFinite(index) ||
          index < 0 ||
          !newType ||
          (!typeMatches[newType] && newType !== "section")
        )
          return;
        try {
          setBlocks((prev) =>
            prev.map((block, i) => {
              if (i !== index) return block;
              const newBlock = {
                ...block,
                type: newType,
                path: primaryPath, // Update path to current primaryPath
                css: {
                  ...(block.css || {}),
                  [newType]: block.css?.[block.type]
                    ? { ...block.css[block.type] }
                    : {
                        ...(defaultCss[newType] || {
                          display: "flex",
                          flexDirection: "column",
                          gap: "16px",
                          padding: "16px",
                        }),
                      },
                },
                data: { elements: [] },
                default_fields: {},
                is_default_value: false,
              };

              switch (newType) {
                case "header":
                case "paragraph":
                case "quote":
                  newBlock.data.elements.push({ field: "text", value: "" });
                  break;
                case "button":
                  newBlock.data.elements.push({
                    field: "text",
                    value: "Click me",
                  });
                  newBlock.data.elements.push({ field: "url", value: "" });
                  break;
                case "list":
                  newBlock.data.elements.push({ field: "items", value: [] });
                  break;
                case "image":
                  newBlock.data.elements.push({ field: "files", value: [] });
                  newBlock.data.elements.push({ field: "caption", value: "" });
                  break;
                case "form":
                  newBlock.data.elements.push({
                    field: "title",
                    value: "New Form",
                  });
                  newBlock.data.elements.push({ field: "fields", value: [] });
                  newBlock.data.elements.push({
                    field: "submitText",
                    value: "Submit",
                  });
                  break;
                case "card":
                  newBlock.data.elements.push({ field: "title", value: "" });
                  newBlock.data.elements.push({
                    field: "description",
                    value: "",
                  });
                  break;
                case "map":
                  newBlock.data.elements.push({ field: "lat", value: null });
                  newBlock.data.elements.push({ field: "lng", value: null });
                  break;
                case "table":
                  newBlock.data.elements.push({ field: "headers", value: [] });
                  newBlock.data.elements.push({ field: "rows", value: [] });
                  break;
                case "faq":
                  newBlock.data.elements.push({ field: "items", value: [] });
                  break;
                case "section":
                  newBlock.data.elements.push({ field: "children", value: [] });
                  break;
                default:
                  throw new Error(`Invalid new block type: ${newType}`);
              }

              if (block.add_display_name) {
                newBlock.data.elements.push({
                  field: "display_name",
                  value: block.display_name || "",
                });
              }

              return newBlock;
            })
          );

          if (blocks[index]) {
            setSelectedBlock(blocks[index]);
          }
          setAnchorElChangeType(null);
          setCurrentBlockIndex(null);
        } catch (error) {
          console.error("Error changing block type:", error);
        }
      },
      [blocks, primaryPath]
    );

    const handleDeleteBlock = useCallback(
      (index) => {
        if (!isFinite(index) || index < 0) return;
        try {
          setBlocks((prev) => prev.filter((_, i) => i !== index));
          setSelectedBlocks((prev) =>
            prev.filter((id) => id !== blocks[index]?.id)
          );
          if (selectedBlock && blocks[index]?.id === selectedBlock.id) {
            setSelectedBlock(null);
          }
        } catch (error) {
          console.error("Error deleting block:", error);
        }
      },
      [selectedBlock, blocks]
    );

    const handleDuplicateBlock = useCallback(
      (index) => {
        if (!isFinite(index) || index < 0 || index >= blocks.length) return;
        try {
          const blockToDuplicate = blocks[index];
          if (!blockToDuplicate) return;
          const newBlock = {
            ...blockToDuplicate,
            id: `block-${uuidv4()}`,
            display_name: `${blockToDuplicate.display_name || ""} (Copy)`,
            path: primaryPath, // Set path to current primaryPath
          };
          setBlocks((prev) => [
            ...prev.slice(0, index + 1),
            newBlock,
            ...prev.slice(index + 1),
          ]);
        } catch (error) {
          console.error("Error duplicating block:", error);
        }
      }, [blocks, primaryPath]
    );

    const handleMergeBlocks = useCallback(() => {
      if (selectedBlocks.length < 2) {
        console.warn("Select at least two blocks to merge");
        return;
      }
      try {
        const blocksToMerge = blocks.filter((block) =>
          selectedBlocks.includes(block.id)
        );
        const firstBlockIndex = blocks.findIndex((block) =>
          selectedBlocks.includes(block.id)
        );

        const newSectionBlock = {
          id: `block-${uuidv4()}`,
          type: "section",
          isMerged: true,
          display_name: "Merged Section",
          path: primaryPath, // Set path to current primaryPath
          children: blocksToMerge,
          is_default_value: false,
          add_display_name: false,
        };

        setBlocks((prev) => {
          const newBlocks = prev.filter(
            (block) => !selectedBlocks.includes(block.id)
          );
          newBlocks.splice(firstBlockIndex, 0, newSectionBlock);
          return newBlocks;
        });

        setSelectedBlock(newSectionBlock);
        setSelectedBlocks([]);
      } catch (error) {
        console.error("Error merging blocks:", error);
      }
    }, [blocks, selectedBlocks, primaryPath]);

    const handleToggleSelectBlock = useCallback((blockId) => {
      setSelectedBlocks((prev) =>
        prev.includes(blockId)
          ? prev.filter((id) => id !== blockId)
          : [...prev, blockId]
      );
    }, []);

    const handleExport = useCallback(() => {
      try {
        const exportedData = blocks.map((block) => ({
          ...block,
          id: block?.id || "",
          type: block?.type || "",
          display_name: block?.display_name || "",
          path: block?.path || primaryPath,
          data: block?.data || { elements: [] },
          css: { ...(block?.css || {}) },
          outerCss: { ...(block?.outerCss || {}) },
          default_fields: { ...(block?.default_fields || {}) },
          is_default_value: !!block?.is_default_value,
          add_display_name: !!block?.add_display_name,
          parentCss: { ...(parentCss || {}) },
        }));

        setJsonData(JSON.stringify(exportedData, null, 2));
        handleSaveAllJson({
          response_object: {
            parameters: exportedData || [],
            primary_path: primaryPath || "",
            display_type: ui_name || "",
            parentCss: { ...(parentCss || {}) },
            cardWidthType:cardWidthType
          },
        });
      } catch (error) {
        console.error("Error exporting data:", error);
        setJsonData("Error exporting data");
      }
    }, [blocks, parentCss, ui_name, primaryPath,cardWidthType]);

    useEffect(() => {
      if (Array.isArray(blocks) && blocks.length) {
        handleExport();
      } else {
        setJsonData("");
        handleSaveAllJson({
          response_object: {
            parameters: [],
            primary_path: primaryPath || "",
            display_type: ui_name || "",
            parentCss: { ...(parentCss || {}) },
          },
        });
      }
    }, [blocks, parentCss, primaryPath, handleExport]);

    const toggleLayoutMode = useCallback(() => {
      setLayoutMode((prev) =>
        prev === "horizontal" ? "vertical" : "horizontal"
      );
      setParentCss((prev) => ({
        ...prev,
        flexDirection: prev.flexDirection === "row" ? "column" : "row",
        gap: prev.gap || "16px",
        padding: prev.padding || "16px",
      }));
    }, []);

    const handleBlockClick = useCallback((block) => {
      setSelectedBlock((prev) => (prev?.id === block?.id ? null : block));
    }, []);

    const openEditModal = useCallback((block, index) => {
      if (!block || !isFinite(index) || index < 0) return;
      try {
        setEditBlock(block);
        setEditBlockIndex(index);
        setShowEditModal(true);
      } catch (error) {
        console.error("Error opening edit modal:", error);
      }
    }, []);

    const handleMenuOpen = useCallback((event, index, type = "add") => {
      try {
        if (type === "add") {
          setAnchorElAdd(event.currentTarget);
        } else if (type === "changeType") {
          setAnchorElChangeType(event.currentTarget);
          setCurrentBlockIndex(index);
        }
      } catch (error) {
        console.error("Error opening menu:", error);
      }
    }, []);

    const handleMenuClose = useCallback((type = "add") => {
      try {
        if (type === "add") {
          setAnchorElAdd(null);
        } else if (type === "changeType") {
          setAnchorElChangeType(null);
          setCurrentBlockIndex(null);
        }
      } catch (error) {
        console.error("Error closing menu:", error);
      }
    }, []);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 5,
        },
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );

    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active.id !== over?.id) {
        setBlocks((prev) => {
          const oldIndex = prev.findIndex((item) => item.id === active.id);
          const newIndex = prev.findIndex((item) => item.id === over.id);
          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    };

    const SortableItem = ({ id, block, index }) => {
      const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
      } = useSortable({ id });
      const [isHovered, setIsHovered] = useState(false);

      const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 999 : "auto",
      };

      return (
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            onClick={() => handleBlockClick(block)}
            style={{
              marginBottom: blockPathErrors[block.id] ? 20:8,
              borderRadius: 6,
              background: "#f4f4f4",
              border: blockPathErrors[block.id] ? "2px solid #d32f2f" : "1px solid #ddd",
              cursor: "grab",
              userSelect: "none",
              position: "relative",

              ...parentCss,
            }}
            title={blockPathErrors[block.id]}
          >
            { isHovered && blockPathErrors[block.id] && (
              <div
                style={{
                  position: "absolute",
                  top: -50,
                  left: 0,
                  right: 0,
                  background: "#d32f2f",
                  color: "#fff",
                  padding: "4px 8px",
                  fontSize: 12,
                  textAlign: "center",
                  borderRadius: "4px 4px 0 0",
                  zIndex: 100,
                }}
              >
                {blockPathErrors[block.id]}
                <Box className="action-buttons" sx={{ display: "flex", alignItems: "center", justifyContent:"end", paddingInline: "10px", gap: "10px" }}>
                 
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteBlock(index);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": { bgcolor: "#d32f2f" },
                      m: 0,
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(block, index);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                      },
                      m: 0,
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              </div>
            )}
            <CardPreview blocks={[block]} />
            {isHovered && !blockPathErrors[block.id] &&  (
              <div
                style={{
                  position: "absolute",
                  top: -30,
                  right: 10,
                  background: "#333",
                  color: "#fff",
                  borderRadius: 4,
                  fontSize: 12,
                  zIndex: 1000,
                  whiteSpace: "nowrap",
                }}
              >
                <Box className="action-buttons" sx={{ display: "flex", alignItems: "center", paddingInline: "10px", gap: "10px" }}>
                  <input
                    type="checkbox"
                    checked={selectedBlocks.includes(block.id)}
                    onChange={() => handleToggleSelectBlock(block.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      accentColor: "#1976d2",
                      cursor: "pointer",
                      width: "16px",
                      height: "16px",
                    }}
                  />
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteBlock(index);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": { bgcolor: "#d32f2f" },
                      m: 0,
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(block, index);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                      },
                      m: 0,
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              </div>
            )}
          </div>
        </div>
      );
    };

 

    // function getArrayPaths(obj, currentPath = '@', seenPaths = new Set()) {
    //   let paths = [];
    //   if (Array.isArray(obj)) {
    //     const pathEntry = { id: currentPath, label: currentPath };
    //     if (!seenPaths.has(currentPath)) {
    //       seenPaths.add(currentPath);
    //       paths.push(pathEntry);
    //     }
    //     obj.forEach((item, index) => {
    //       const itemPath = `${currentPath}[${index}]`;
    //       paths = paths.concat(getArrayPaths(item, itemPath, seenPaths));
    //     });
    //   } else if (typeof obj === 'object' && obj !== null) {
    //     Object.keys(obj).forEach(key => {
    //       const keyPath = currentPath === '@' ? key : `${currentPath}.${key}`;
    //       paths = paths.concat(getArrayPaths(obj[key], keyPath, seenPaths));
    //     });
    //   }
    //   return paths;
    // }

function getArrayPaths(obj, currentPath = '@', seenPaths = new Set()) {
  let paths = [];

  // Detect Arrays
  if (Array.isArray(obj)) {
    if (currentPath !== '@' && !seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push({ id: currentPath, label: currentPath });
    }

    obj.forEach((item, index) => {
      const itemPath = `${currentPath}[${index}]`;
      paths = paths.concat(getArrayPaths(item, itemPath, seenPaths));
    });

  // Detect Objects
  } else if (typeof obj === 'object' && obj !== null) {
    if (currentPath !== '@' && !seenPaths.has(currentPath)) {
      seenPaths.add(currentPath);
      paths.push({ id: currentPath, label: currentPath });
    }

    Object.keys(obj).forEach(key => {
      const keyPath = currentPath === '@' ? key : `${currentPath}.${key}`;
      paths = paths.concat(getArrayPaths(obj[key], keyPath, seenPaths));
    });
  }

  return paths;
}


    return (
      <ErrorBoundary>
        <Box sx={{ mb: "16px", display: "flex", gap: "8px", alignItems: "center" }}>
          <div>
            <p>Primary Path</p>
            <Autocomplete
              options={getArrayPaths(apiData)}
              getOptionLabel={(option) => option.label}
              value={getArrayPaths(apiData).find(p => p.id === primaryPath) || null}
              onChange={(e, newValue) => {
                setPrimaryPath(newValue ? newValue.id : '');
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" size="small" placeholder="Select Path" />
              )}
              renderOption={(props, option) => (
                <Tooltip
                  title={`Path Value: ${JSON.stringify(getValueFromPath(option.label, apiData), null, 2)}...`}
                  placement="right"
                  sx={{ background: "white",overflow:"auto" }}
                  arrow
                  key={JSON.stringify(option)}
                >
                  <li {...props}>{option.label}</li>
                </Tooltip>
              )}
              style={{ minWidth: "200px", overflow: "auto" }}
            />
          </div>
          <SuperAppButton
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineIcon />}
            onClick={(e) => handleMenuOpen(e, null, "add")}
            sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
          >
            Add Block
          </SuperAppButton>
          <Menu
            anchorEl={anchorElAdd}
            open={Boolean(anchorElAdd)}
            onClose={() => handleMenuClose("add")}
            PaperProps={{ sx: { bgcolor: "#202345", color: "#fff" } }}
          >
            {Array.isArray(blockTypes) &&
              blockTypes.map(({ type, defaultLabel }) => (
                <MenuItem
                  key={type}
                  onClick={() => handleAddNewBlock(type)}
                  sx={{
                    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
                  }}
                >
                  {defaultLabel}
                </MenuItem>
              ))}
          </Menu>
          <SuperAppButton
            variant="outlined"
            onClick={() => setShowAddMoreModal(true)}
            sx={{
              color: "#fff",
              borderColor: "#fff",
              "&:hover": {
                borderColor: "#90caf9",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Add More
          </SuperAppButton>
          <SuperAppButton
            variant="outlined"
            onClick={() => setShowEditorModal(true)}
            sx={{
              color: "#fff",
              borderColor: "#fff",
              "&:hover": {
                borderColor: "#90caf9",
                bgcolor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            Preview
          </SuperAppButton>
          <SuperAppButton
            variant="contained"
            color="primary"
            startIcon={<MergeTypeIcon />}
            onClick={handleMergeBlocks}
            disabled={selectedBlocks.length < 2}
            sx={{ bgcolor: "#1976d2", "&:hover": { bgcolor: "#1565c0" } }}
          >
            Merge Blocks
          </SuperAppButton>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: layoutMode === "horizontal" ? "row" : "column",
            height: "100vh",
            gap: "10px",
          }}
        >
          <Box
            sx={{
              width: "40%",
              bgcolor: "#202345",
              overflowY: "auto",
              borderRight:
                layoutMode === "horizontal" ? "1px solid #2c2f5b" : "none",
              borderBottom:
                layoutMode === "vertical" ? "1px solid #2c2f5b" : "none",
            }}
          >
            <div style={{ maxWidth: 300, margin: "2rem auto" }}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={blocks.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {Array.isArray(blocks) &&
                    blocks.map((block, index) => (
                      <SortableItem
                        key={block.id}
                        id={block.id}
                        block={block}
                        index={index}
                      />
                    ))}
                </SortableContext>
              </DndContext>
            </div>
          </Box>
          <Box
            sx={{
              flex: layoutMode === "horizontal" ? "1" : "3",
              overflow: "auto",
              bgcolor: "#1a1d3d",
            }}
          >
            <CssEditor
              selectedBlock={selectedBlock}
              updateBlock={(updatedBlock) => {
                if (!updatedBlock || !selectedBlock?.id) return;
                const index = blocks.findIndex(
                  (b) => b.id === selectedBlock.id
                );
                if (index !== -1) {
                  updateBlock(index, updatedBlock);
                }
              }}
              setcardWidthType={setcardWidthType}
              cardWidthType={cardWidthType}
              parentCss={parentCss}
              updateParentCss={setParentCss}
            />
          </Box>
        </Box>

        {showEditorModal && (
          <BlockEditorModal
            show={showEditorModal}
            onHide={() => setShowEditorModal(false)}
            blocks={blocks}
            parentCss={parentCss}
          />
        )}
        <ExportModal
          show={showExportModal}
          onHide={() => setShowExportModal(false)}
          jsonData={jsonData}
        />
        <AddMoreModal
          show={showAddMoreModal}
          onHide={() => setShowAddMoreModal(false)}
          initialJsons2={dynamicInitialJsons2}
          onAdd={addMoreBlocks}
          blocks={blocks}
        />
        {showEditModal && (
          <BlockEditModal
            show={showEditModal}
            onHide={() => {
              setShowEditModal(false);
              setEditBlock(null);
              setEditBlockIndex(null);
            }}
            block={editBlock}
            blockIndex={editBlockIndex}
            updateBlock={updateBlock}
            apiData={apiData}
            primaryPath={primaryPath}
          />
        )}
      </ErrorBoundary>
    );
  }
);

export default CustomEditor;
